import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Columns3,
  Italic,
  List,
  ListOrdered,
  Palette,
  Plus,
  Redo2,
  Rows3,
  Table2,
  Trash2,
  Type,
  Undo2,
} from "lucide-react";

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

type TableContext = {
  active: boolean;
  canAddRow: boolean;
  canDeleteRow: boolean;
  canAddColumn: boolean;
  canDeleteColumn: boolean;
  hasMergedCells: boolean;
  canUnmergeCell: boolean;
};

type TableColorScope = "cell" | "row" | "column" | "table" | "selection";
type TableTextAlign = "left" | "center" | "right";
type TableToolbarPanel = "table" | "background" | "border" | "size" | null;

type TableResizeOverlay = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type TableCellFormat = {
  background?: string;
  color?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  textAlign?: TableTextAlign;
  borderColor?: string;
  borderWidth?: number;
  width?: number;
  height?: number;
  rowSpan?: number;
  colSpan?: number;
};

type InlineTextFormat = {
  color?: string;
  fontSize?: number;
};

type ManualMarkdownNode = {
  type?: string;
  value?: string;
  children?: ManualMarkdownNode[];
  data?: {
    hProperties?: Record<string, unknown>;
    [key: string]: unknown;
  };
};

const TABLE_COLOR_MARKER = "inpronexus-table-colors";
const TEXT_FORMAT_MARKER = "inpronexus-text-format";
const TEXT_SIZE_OPTIONS = [9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32] as const;

const emptyTableContext: TableContext = {
  active: false,
  canAddRow: false,
  canDeleteRow: false,
  canAddColumn: false,
  canDeleteColumn: false,
  hasMergedCells: false,
  canUnmergeCell: false,
};

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function renderInlineMarkdown(value: string): string {
  const tokenPattern = /<!--\s*(\/?)inpronexus-text-format(?::\s*(.*?))?\s*-->/gi;
  let html = "";
  let cursor = 0;
  let match: RegExpExecArray | null;

  const renderPlain = (plain: string): string => {
    let rendered = escapeHtml(plain);
    rendered = rendered.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    rendered = rendered.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
    return rendered.replaceAll("\n", "<br>");
  };

  while ((match = tokenPattern.exec(value)) !== null) {
    html += renderPlain(value.slice(cursor, match.index));
    if (match[1] === "/") {
      html += "</span>";
    } else {
      const format = parseInlineTextFormat(match[2] ?? "");
      const style = inlineTextStyle(format);
      const attributes = [
        'data-manual-inline="1"',
        format.color ? `data-manual-fg="${format.color}"` : "",
        format.fontSize ? `data-manual-fs="${format.fontSize}"` : "",
        style ? `style="${style};"` : "",
      ].filter(Boolean).join(" ");
      html += `<span ${attributes}>`;
    }
    cursor = tokenPattern.lastIndex;
  }

  html += renderPlain(value.slice(cursor));
  return html;
}

function renderTableCellMarkdown(value: string): string {
  const normalized = value.replace(/<br\s*\/?>/gi, "\n");
  const lines = normalized.split("\n");
  if (lines.length === 1) return renderInlineMarkdown(lines[0] ?? "");

  const blocks: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? "";
    const unordered = line.match(/^\s*[•*-]\s+(.+)$/);
    if (unordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = (lines[index] ?? "").match(/^\s*[•*-]\s+(.+)$/);
        if (!match) break;
        items.push(`<li>${renderInlineMarkdown(match[1])}</li>`);
        index += 1;
      }
      blocks.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    const ordered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (ordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = (lines[index] ?? "").match(/^\s*\d+\.\s+(.+)$/);
        if (!match) break;
        items.push(`<li>${renderInlineMarkdown(match[1])}</li>`);
        index += 1;
      }
      blocks.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    blocks.push(line.trim() ? `<div>${renderInlineMarkdown(line)}</div>` : "<div><br></div>");
    index += 1;
  }

  return blocks.join("");
}

function splitMarkdownTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells: string[] = [];
  let current = "";
  let escaped = false;

  for (const character of trimmed) {
    if (escaped) {
      current += character;
      escaped = false;
      continue;
    }
    if (character === "\\") {
      escaped = true;
      continue;
    }
    if (character === "|") {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += character;
  }
  cells.push(current.trim());

  return cells;
}

function isTableSeparator(line: string): boolean {
  const cells = splitMarkdownTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isTableStart(lines: string[], index: number): boolean {
  return index + 1 < lines.length
    && lines[index].includes("|")
    && isTableSeparator(lines[index + 1]);
}

function isHexColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

function parseTableColorMarker(line: string): Map<string, TableCellFormat> | null {
  const match = line.trim().match(/^<!--\s*inpronexus-table-colors:\s*(.*?)\s*-->$/i);
  if (!match) return null;

  const formats = new Map<string, TableCellFormat>();
  match[1].split(";").forEach((entry) => {
    const item = entry.trim();
    if (!item) return;
    const coordinateMatch = item.match(/^(\d+),(\d+)=(.+)$/i);
    if (!coordinateMatch) return;

    const key = `${Number(coordinateMatch[1])},${Number(coordinateMatch[2])}`;
    const rawFormat = coordinateMatch[3].trim();
    if (isHexColor(rawFormat)) {
      formats.set(key, { background: rawFormat.toLowerCase() });
      return;
    }

    const format: TableCellFormat = {};
    rawFormat.split(",").forEach((part) => {
      const [rawName, rawValue] = part.split(":", 2).map((value) => value?.trim() ?? "");
      if (rawName === "bg" && isHexColor(rawValue)) format.background = rawValue.toLowerCase();
      if (rawName === "fg" && isHexColor(rawValue)) format.color = rawValue.toLowerCase();
      if (rawName === "bc" && isHexColor(rawValue)) format.borderColor = rawValue.toLowerCase();
      if (rawName === "b" && (rawValue === "0" || rawValue === "1")) format.bold = rawValue === "1";
      if (rawName === "i" && (rawValue === "0" || rawValue === "1")) format.italic = rawValue === "1";
      if (rawName === "al" && (rawValue === "l" || rawValue === "c" || rawValue === "r")) {
        format.textAlign = rawValue === "c" ? "center" : rawValue === "r" ? "right" : "left";
      }
      if (rawName === "fs" && /^\d{1,2}$/.test(rawValue)) {
        const size = Number(rawValue);
        if (size >= 8 && size <= 48) format.fontSize = size;
      }
      if (rawName === "bw" && /^\d$/.test(rawValue)) {
        const width = Number(rawValue);
        if (width >= 0 && width <= 8) format.borderWidth = width;
      }
      if (rawName === "w" && /^\d{2,4}$/.test(rawValue)) {
        const width = Number(rawValue);
        if (width >= 40 && width <= 1200) format.width = width;
      }
      if (rawName === "h" && /^\d{2,4}$/.test(rawValue)) {
        const height = Number(rawValue);
        if (height >= 24 && height <= 600) format.height = height;
      }
      if (rawName === "rs" && /^\d{1,2}$/.test(rawValue)) {
        const span = Number(rawValue);
        if (span >= 2 && span <= 24) format.rowSpan = span;
      }
      if (rawName === "cs" && /^\d{1,2}$/.test(rawValue)) {
        const span = Number(rawValue);
        if (span >= 2 && span <= 24) format.colSpan = span;
      }
    });
    if (
      format.background
      || format.color
      || format.fontSize
      || format.bold !== undefined
      || format.italic !== undefined
      || format.textAlign
      || format.borderColor
      || format.borderWidth !== undefined
      || format.width
      || format.height
      || format.rowSpan
      || format.colSpan
    ) formats.set(key, format);
  });
  return formats;
}

function tableColorMarker(table: HTMLTableElement): string {
  const formats: string[] = [];
  Array.from(table.rows).forEach((row, rowIndex) => {
    Array.from(row.cells).forEach((cell, cellIndex) => {
      const parts: string[] = [];
      if (cell.dataset.manualCovered === "1") return;
      const background = cell.dataset.manualBg?.toLowerCase() ?? "";
      const color = cell.dataset.manualFg?.toLowerCase() ?? "";
      const borderColor = cell.dataset.manualBc?.toLowerCase() ?? "";
      const fontSize = Number(cell.dataset.manualFs ?? "");
      const bold = cell.dataset.manualBold;
      const italic = cell.dataset.manualItalic;
      const borderWidth = cell.dataset.manualBw !== undefined ? Number(cell.dataset.manualBw) : Number.NaN;
      const width = Number(cell.dataset.manualW ?? "");
      const height = Number(cell.dataset.manualH ?? "");
      const rowSpan = Math.max(1, cell.rowSpan || 1);
      const colSpan = Math.max(1, cell.colSpan || 1);
      if (isHexColor(background)) parts.push(`bg:${background}`);
      if (isHexColor(color)) parts.push(`fg:${color}`);
      if (Number.isFinite(fontSize) && fontSize >= 8 && fontSize <= 48) parts.push(`fs:${fontSize}`);
      if (bold === "0" || bold === "1") parts.push(`b:${bold}`);
      if (italic === "0" || italic === "1") parts.push(`i:${italic}`);
      const textAlign = cell.dataset.manualAlign;
      if (textAlign === "left" || textAlign === "center" || textAlign === "right") {
        parts.push(`al:${textAlign === "center" ? "c" : textAlign === "right" ? "r" : "l"}`);
      }
      if (isHexColor(borderColor)) parts.push(`bc:${borderColor}`);
      if (Number.isFinite(borderWidth) && borderWidth >= 0 && borderWidth <= 8) parts.push(`bw:${borderWidth}`);
      if (Number.isFinite(width) && width >= 40 && width <= 1200) parts.push(`w:${width}`);
      if (Number.isFinite(height) && height >= 24 && height <= 600) parts.push(`h:${height}`);
      if (rowSpan > 1) parts.push(`rs:${rowSpan}`);
      if (colSpan > 1) parts.push(`cs:${colSpan}`);
      if (parts.length > 0) formats.push(`${rowIndex},${cellIndex}=${parts.join(",")}`);
    });
  });
  return formats.length > 0 ? `<!-- ${TABLE_COLOR_MARKER}: ${formats.join(";")} -->` : "";
}

function mergedCellCoverage(formats: Map<string, TableCellFormat> | null): Set<string> {
  const covered = new Set<string>();
  formats?.forEach((format, key) => {
    const [rowIndex, cellIndex] = key.split(",").map(Number);
    const rowSpan = Math.max(1, format.rowSpan ?? 1);
    const colSpan = Math.max(1, format.colSpan ?? 1);
    for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
      for (let columnOffset = 0; columnOffset < colSpan; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        covered.add(`${rowIndex + rowOffset},${cellIndex + columnOffset}`);
      }
    }
  });
  return covered;
}

function hasIndependentCellWidths(
  formats: Map<string, TableCellFormat> | null,
  rowCount: number,
  columnCount: number,
): boolean {
  if (!formats || rowCount <= 0 || columnCount <= 0) return false;
  if (Array.from(formats.values()).some((format) => (format.rowSpan ?? 1) > 1 || (format.colSpan ?? 1) > 1)) return false;

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const widths = Array.from({ length: rowCount }, (_, rowIndex) => formats.get(`${rowIndex},${columnIndex}`)?.width);
    const defined = widths.filter((width): width is number => Number.isFinite(width));
    if (defined.length === 0) continue;
    if (defined.length !== rowCount || new Set(defined).size > 1) return true;
  }
  return false;
}

function cellFormatAttributes(
  formats: Map<string, TableCellFormat> | null,
  rowIndex: number,
  cellIndex: number,
  covered: Set<string>,
): string {
  const key = `${rowIndex},${cellIndex}`;
  if (covered.has(key)) return ' data-manual-covered="1" aria-hidden="true" style="display: none;"';

  const format = formats?.get(key);
  if (!format) return "";

  const attributes: string[] = [];
  const styles: string[] = [];
  if (format.background && isHexColor(format.background)) {
    attributes.push(`data-manual-bg="${format.background}"`);
    styles.push(`background-color: ${format.background}`);
  }
  if (format.color && isHexColor(format.color)) {
    attributes.push(`data-manual-fg="${format.color}"`);
    styles.push(`color: ${format.color}`);
  }
  if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) {
    attributes.push(`data-manual-fs="${format.fontSize}"`);
    styles.push(`font-size: ${format.fontSize}pt`);
  }
  if (format.bold !== undefined) {
    attributes.push(`data-manual-bold="${format.bold ? "1" : "0"}"`);
    styles.push(`font-weight: ${format.bold ? "700" : "400"}`);
  }
  if (format.italic !== undefined) {
    attributes.push(`data-manual-italic="${format.italic ? "1" : "0"}"`);
    styles.push(`font-style: ${format.italic ? "italic" : "normal"}`);
  }
  if (format.textAlign) {
    attributes.push(`data-manual-align="${format.textAlign}"`);
    styles.push(`text-align: ${format.textAlign}`);
  }
  if (format.borderColor && isHexColor(format.borderColor)) {
    attributes.push(`data-manual-bc="${format.borderColor}"`);
  }
  if (format.borderWidth !== undefined && format.borderWidth >= 0 && format.borderWidth <= 8) {
    attributes.push(`data-manual-bw="${format.borderWidth}"`);
    styles.push(format.borderWidth === 0
      ? "border: 0"
      : `border: ${format.borderWidth}px solid ${format.borderColor && isHexColor(format.borderColor) ? format.borderColor : "#cfdcd7"}`);
  }
  if (format.width && format.width >= 40 && format.width <= 1200) {
    attributes.push(`data-manual-w="${format.width}"`);
    styles.push(`width: ${format.width}px`, `min-width: ${format.width}px`);
  }
  if (format.height && format.height >= 24 && format.height <= 600) {
    attributes.push(`data-manual-h="${format.height}"`);
    styles.push(`height: ${format.height}px`);
  }
  if (format.rowSpan && format.rowSpan > 1) {
    attributes.push(`data-manual-rs="${format.rowSpan}"`, `rowspan="${format.rowSpan}"`);
  }
  if (format.colSpan && format.colSpan > 1) {
    attributes.push(`data-manual-cs="${format.colSpan}"`, `colspan="${format.colSpan}"`);
  }
  if (styles.length > 0) attributes.push(`style="${styles.join("; ")};"`);
  return attributes.length > 0 ? ` ${attributes.join(" ")}` : "";
}

function parseInlineTextFormat(raw: string): InlineTextFormat {
  const format: InlineTextFormat = {};
  raw.split(";").forEach((entry) => {
    const [name, value] = entry.split("=", 2).map((part) => part?.trim() ?? "");
    if (name === "fg" && isHexColor(value)) format.color = value.toLowerCase();
    if (name === "fs" && /^\d{1,2}$/.test(value)) {
      const size = Number(value);
      if (size >= 8 && size <= 48) format.fontSize = size;
    }
  });
  return format;
}

function inlineTextFormatMarker(format: InlineTextFormat): string {
  const parts: string[] = [];
  if (format.color && isHexColor(format.color)) parts.push(`fg=${format.color.toLowerCase()}`);
  if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) parts.push(`fs=${format.fontSize}`);
  return parts.length > 0 ? `<!-- ${TEXT_FORMAT_MARKER}: ${parts.join(";")} -->` : "";
}

function inlineTextStyle(format: InlineTextFormat): string {
  const styles: string[] = [];
  if (format.color && isHexColor(format.color)) styles.push(`color: ${format.color}`);
  if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) styles.push(`font-size: ${format.fontSize}pt`);
  return styles.join("; ");
}

function cssColorToHex(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  if (isHexColor(normalized)) return normalized;

  const rgb = normalized.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/);
  if (!rgb) return null;
  if (rgb[4] !== undefined && Number(rgb[4]) === 0) return "#ffffff";

  const toHex = (part: string) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0");
  return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`;
}

export function remarkManualTableColors() {
  return (tree: unknown): void => {
    const root = tree as ManualMarkdownNode;

    const transformInlineFormatting = (node: ManualMarkdownNode): void => {
      if (!Array.isArray(node.children)) return;
      node.children.forEach(transformInlineFormatting);

      const output: ManualMarkdownNode[] = [];
      const stack: Array<{ node: ManualMarkdownNode; parent: ManualMarkdownNode[] }> = [];
      let current = output;

      node.children.forEach((child) => {
        if (child.type === "html" && typeof child.value === "string") {
          if (/^<br\s*\/?>$/i.test(child.value.trim())) {
            current.push({ type: "break" });
            return;
          }
          const startMatch = child.value.trim().match(/^<!--\s*inpronexus-text-format:\s*(.*?)\s*-->$/i);
          if (startMatch) {
            const format = parseInlineTextFormat(startMatch[1] ?? "");
            const style = inlineTextStyle(format);
            const wrapper: ManualMarkdownNode = {
              type: "manualTextFormat",
              children: [],
              data: {
                hName: "span",
                hProperties: style ? { style } : {},
              },
            };
            current.push(wrapper);
            stack.push({ node: wrapper, parent: current });
            current = wrapper.children ?? [];
            return;
          }
          if (/^<!--\s*\/inpronexus-text-format\s*-->$/i.test(child.value.trim()) && stack.length > 0) {
            const frame = stack.pop();
            current = frame?.parent ?? output;
            return;
          }
        }
        current.push(child);
      });

      node.children = output;
    };

    transformInlineFormatting(root);
    if (!Array.isArray(root.children)) return;

    for (let index = 0; index < root.children.length; index += 1) {
      const markerNode = root.children[index];
      if (markerNode.type !== "html" || typeof markerNode.value !== "string") continue;
      const formats = parseTableColorMarker(markerNode.value);
      if (formats === null) continue;

      const tableNode = root.children[index + 1];
      if (tableNode?.type === "table" && Array.isArray(tableNode.children)) {
        const tableRowCount = tableNode.children.length;
        const tableColumnCount = Math.max(0, ...tableNode.children.map((row) => Array.isArray(row.children) ? row.children.length : 0));
        if (hasIndependentCellWidths(formats, tableRowCount, tableColumnCount)) {
          tableNode.data = tableNode.data ?? {};
          tableNode.data.hProperties = { ...(tableNode.data.hProperties ?? {}), "data-manual-free-width": "1" };
        }
        const covered = mergedCellCoverage(formats);
        tableNode.children.forEach((row, rowIndex) => {
          if (!Array.isArray(row.children)) return;
          row.children.forEach((cell, cellIndex) => {
            const key = `${rowIndex},${cellIndex}`;
            const format = formats.get(key);
            const isCovered = covered.has(key);
            if (!format && !isCovered) return;
            const styles: string[] = [];
            const hProperties: Record<string, unknown> = { ...(cell.data?.hProperties ?? {}) };
            if (isCovered) {
              styles.push("display: none");
              hProperties["aria-hidden"] = "true";
            }
            if (format?.background && isHexColor(format.background)) styles.push(`background-color: ${format.background}`);
            if (format?.color && isHexColor(format.color)) styles.push(`color: ${format.color}`);
            if (format?.fontSize && format.fontSize >= 8 && format.fontSize <= 48) styles.push(`font-size: ${format.fontSize}pt`);
            if (format?.bold !== undefined) styles.push(`font-weight: ${format.bold ? "700" : "400"}`);
            if (format?.italic !== undefined) styles.push(`font-style: ${format.italic ? "italic" : "normal"}`);
            if (format?.textAlign) styles.push(`text-align: ${format.textAlign}`);
            if (format?.borderWidth !== undefined && format.borderWidth >= 0 && format.borderWidth <= 8) {
              styles.push(format.borderWidth === 0
                ? "border: 0"
                : `border: ${format.borderWidth}px solid ${format.borderColor && isHexColor(format.borderColor) ? format.borderColor : "#cfdcd7"}`);
            }
            if (format?.width && format.width >= 40 && format.width <= 1200) {
              styles.push(`width: ${format.width}px`, `min-width: ${format.width}px`);
              hProperties["data-manual-w"] = String(format.width);
            }
            if (format?.height && format.height >= 24 && format.height <= 600) styles.push(`height: ${format.height}px`);
            if (format?.rowSpan && format.rowSpan > 1) hProperties.rowSpan = format.rowSpan;
            if (format?.colSpan && format.colSpan > 1) hProperties.colSpan = format.colSpan;
            if (styles.length > 0) hProperties.style = `${styles.join("; ")};`;
            cell.data = cell.data ?? {};
            cell.data.hProperties = hProperties;
          });
        });
      }

      root.children.splice(index, 1);
      index -= 1;
    }
  };
}

function markdownToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[] = [];
  let index = 0;
  let pendingTableColors: Map<string, TableCellFormat> | null = null;

  while (index < lines.length) {
    const line = lines[index];
    const marker = parseTableColorMarker(line);

    if (marker !== null) {
      pendingTableColors = marker;
      index += 1;
      continue;
    }

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (isTableStart(lines, index)) {
      const headers = splitMarkdownTableRow(lines[index]);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].trim().startsWith("|") && lines[index].includes("|")) {
        rows.push(splitMarkdownTableRow(lines[index]));
        index += 1;
      }
      const covered = mergedCellCoverage(pendingTableColors);
      const independentWidths = hasIndependentCellWidths(pendingTableColors, rows.length + 1, headers.length);
      const headerHtml = headers
        .map((cell, cellIndex) => `<th${cellFormatAttributes(pendingTableColors, 0, cellIndex, covered)}>${renderTableCellMarkdown(cell)}</th>`)
        .join("");
      const bodyHtml = rows
        .map((row, rowIndex) => `<tr>${headers.map((_, cellIndex) => `<td${cellFormatAttributes(pendingTableColors, rowIndex + 1, cellIndex, covered)}>${renderTableCellMarkdown(row[cellIndex] ?? "")}</td>`).join("")}</tr>`)
        .join("");
      blocks.push(`<table${independentWidths ? ' data-manual-free-width="1"' : ""}><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`);
      pendingTableColors = null;
      continue;
    }

    pendingTableColors = null;

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^[-*]\s+/, ""));
        index += 1;
      }
      blocks.push(`<ul>${items.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join("")}</ul>`);
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\d+\.\s+/, ""));
        index += 1;
      }
      blocks.push(`<ol>${items.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join("")}</ol>`);
      continue;
    }

    const paragraph: string[] = [line];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !isTableStart(lines, index)
      && !/^(#{1,6})\s+/.test(lines[index])
      && !/^[-*]\s+/.test(lines[index])
      && !/^\d+\.\s+/.test(lines[index])
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(`<p>${renderInlineMarkdown(paragraph.join("\n"))}</p>`);
  }

  return blocks.join("");
}

function inlineToMarkdown(node: Node, inTable = false): string {
  if (node.nodeType === Node.TEXT_NODE) {
    const value = (node.textContent ?? "").replace(/\u00a0/g, " ");
    return inTable ? value.replaceAll("|", "\\|") : value;
  }

  if (!(node instanceof HTMLElement)) return "";

  const children = Array.from(node.childNodes).map((child) => inlineToMarkdown(child, inTable)).join("");
  switch (node.tagName) {
    case "BR":
      return "\n";
    case "STRONG":
    case "B":
      return children ? `**${children}**` : "";
    case "EM":
    case "I":
      return children ? `*${children}*` : "";
    case "SPAN": {
      if (node.dataset.manualInline !== "1") return children;
      const color = node.dataset.manualFg;
      const fontSize = Number(node.dataset.manualFs ?? "");
      const marker = inlineTextFormatMarker({
        color: color && isHexColor(color) ? color : undefined,
        fontSize: Number.isFinite(fontSize) && fontSize >= 8 && fontSize <= 48 ? fontSize : undefined,
      });
      return marker && children ? `${marker}${children}<!-- /${TEXT_FORMAT_MARKER} -->` : children;
    }
    default:
      return children;
  }
}

function tableCellToMarkdown(cell: HTMLTableCellElement): string {
  let output = "";
  const appendBreak = () => {
    if (output && !output.endsWith("<br>")) output += "<br>";
  };

  const appendList = (list: HTMLUListElement | HTMLOListElement, depth = 0) => {
    const ordered = list.tagName === "OL";
    Array.from(list.children)
      .filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
      .forEach((item, itemIndex) => {
        appendBreak();
        const inline = Array.from(item.childNodes)
          .filter((child) => !(child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")))
          .map((child) => inlineToMarkdown(child, true))
          .join("")
          .replace(/\n+/g, " ")
          .trim();
        const indent = depth > 0 ? "\u00a0\u00a0".repeat(depth) : "";
        const prefix = ordered ? `${itemIndex + 1}.` : "•";
        output += `${indent}${prefix}${inline ? ` ${inline}` : ""}`;
        Array.from(item.children)
          .filter((child): child is HTMLUListElement | HTMLOListElement => child instanceof HTMLUListElement || child instanceof HTMLOListElement)
          .forEach((nested) => appendList(nested, depth + 1));
      });
  };

  const visit = (node: Node) => {
    if (node instanceof HTMLBRElement) {
      appendBreak();
      return;
    }
    if (node instanceof HTMLUListElement || node instanceof HTMLOListElement) {
      appendList(node);
      appendBreak();
      return;
    }
    if (node instanceof HTMLElement && (node.tagName === "DIV" || node.tagName === "P")) {
      appendBreak();
      Array.from(node.childNodes).forEach(visit);
      appendBreak();
      return;
    }
    output += inlineToMarkdown(node, true).replace(/\n+/g, "<br>");
  };

  Array.from(cell.childNodes).forEach(visit);
  return output
    .replace(/(?:<br>){3,}/g, "<br><br>")
    .replace(/^(?:<br>)+|(?:<br>)+$/g, "")
    .trim();
}

function tableToMarkdown(table: HTMLTableElement): string {
  const rows = Array.from(table.rows);
  if (rows.length === 0) return "";

  const columnCount = Math.max(1, ...rows.map((row) => row.cells.length));
  const serializeRow = (row: HTMLTableRowElement | null) => {
    const cells = Array.from({ length: columnCount }, (_, index) => {
      const cell = row?.cells[index];
      return cell ? tableCellToMarkdown(cell) : "";
    });
    return `| ${cells.join(" | ")} |`;
  };

  const header = rows[0];
  const separator = `| ${Array.from({ length: columnCount }, () => "---").join(" | ")} |`;
  const body = rows.slice(1).map((row) => serializeRow(row));
  return [tableColorMarker(table), serializeRow(header), separator, ...body].filter(Boolean).join("\n");
}

function listItemInlineToMarkdown(item: HTMLLIElement): string {
  return Array.from(item.childNodes)
    .filter((child) => !(child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")))
    .map((child) => inlineToMarkdown(child))
    .join("")
    .replace(/\n+/g, " ")
    .trim();
}

function listToMarkdown(list: HTMLUListElement | HTMLOListElement): string {
  const ordered = list.tagName === "OL";
  const lines: string[] = [];

  Array.from(list.children)
    .filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
    .forEach((item, itemIndex) => {
      const text = listItemInlineToMarkdown(item);
      const prefix = ordered ? `${itemIndex + 1}.` : "-";
      if (text) lines.push(`${prefix} ${text}`);

      Array.from(item.children)
        .filter((child): child is HTMLUListElement | HTMLOListElement => child instanceof HTMLUListElement || child instanceof HTMLOListElement)
        .forEach((nestedList) => {
          const nested = listToMarkdown(nestedList);
          if (nested) lines.push(nested);
        });
    });

  return lines.join("\n");
}

function nodeToMarkdownBlocks(node: Node): string[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? "").trim();
    return text ? [text] : [];
  }
  if (!(node instanceof HTMLElement)) return [];

  const tag = node.tagName;
  if (tag === "TABLE") {
    const table = tableToMarkdown(node as HTMLTableElement);
    return table ? [table] : [];
  }
  if (/^H[1-6]$/.test(tag)) {
    const level = Number(tag.slice(1));
    const text = inlineToMarkdown(node).trim();
    return text ? [`${"#".repeat(level)} ${text}`] : [];
  }
  if (tag === "UL" || tag === "OL") {
    const list = listToMarkdown(node as HTMLUListElement | HTMLOListElement);
    return list ? [list] : [];
  }

  const blockChildTags = new Set(["DIV", "P", "UL", "OL", "TABLE", "H1", "H2", "H3", "H4", "H5", "H6"]);
  const hasBlockChildren = Array.from(node.children).some((child) => blockChildTags.has(child.tagName));
  if (!hasBlockChildren) {
    const text = inlineToMarkdown(node).trim();
    return text ? [text] : [];
  }

  const blocks: string[] = [];
  let inlineBuffer = "";
  const flushInline = () => {
    const text = inlineBuffer.trim();
    if (text) blocks.push(text);
    inlineBuffer = "";
  };

  Array.from(node.childNodes).forEach((child) => {
    if (child instanceof HTMLElement && blockChildTags.has(child.tagName)) {
      flushInline();
      blocks.push(...nodeToMarkdownBlocks(child));
      return;
    }
    inlineBuffer += inlineToMarkdown(child);
  });
  flushInline();

  return blocks;
}

function editorToMarkdown(root: HTMLElement): string {
  return Array.from(root.childNodes)
    .flatMap((node) => nodeToMarkdownBlocks(node))
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

function ToolbarButton({
  label,
  disabled,
  active = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`manual-si-visual-editor-button ${active ? "is-active" : ""}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(event: ReactMouseEvent<HTMLButtonElement>) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function ManualSistemaIntegradoVisualEditor({ value, onChange, disabled = false }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const selectedCellRef = useRef<HTMLTableCellElement | null>(null);
  const selectedTableCellsRef = useRef<Set<HTMLTableCellElement>>(new Set());
  const selectedTableRef = useRef<HTMLTableElement | null>(null);
  const selectionAnchorCellRef = useRef<HTMLTableCellElement | null>(null);
  const dragSelectionRef = useRef<{ table: HTMLTableElement; startCell: HTMLTableCellElement } | null>(null);
  const resizeDragRef = useRef<{ axis: "width" | "height" } | null>(null);
  const editingCellRef = useRef<HTMLTableCellElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const initializedRef = useRef(false);
  const lastRenderedValueRef = useRef("");
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const [tableContext, setTableContext] = useState<TableContext>(emptyTableContext);
  const [tableBuilderOpen, setTableBuilderOpen] = useState(false);
  const [tableToolbarPanel, setTableToolbarPanel] = useState<TableToolbarPanel>(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [tableColorScope, setTableColorScope] = useState<TableColorScope>("cell");
  const [selectedColor, setSelectedColor] = useState("#edf7f2");
  const [selectedTextColor, setSelectedTextColor] = useState("#334155");
  const [selectedTextSize, setSelectedTextSize] = useState(11);
  const [selectedBorderColor, setSelectedBorderColor] = useState("#64748b");
  const [selectedBorderWidth, setSelectedBorderWidth] = useState(1);
  const [selectedColumnWidth, setSelectedColumnWidth] = useState(160);
  const [selectedRowHeight, setSelectedRowHeight] = useState(44);
  const [selectedTableCellCount, setSelectedTableCellCount] = useState(0);
  const [tableResizeOverlay, setTableResizeOverlay] = useState<TableResizeOverlay | null>(null);
  const [hasCopiedCellStyle, setHasCopiedCellStyle] = useState(false);
  const [tableFeedback, setTableFeedback] = useState("");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const copiedCellStyleRef = useRef<TableCellFormat | null>(null);
  const textColorTargetCellsRef = useRef<HTMLTableCellElement[]>([]);
  const textColorTargetRangeRef = useRef<Range | null>(null);

  const updateHistoryState = () => {
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current >= 0 && historyIndexRef.current < historyRef.current.length - 1);
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (initializedRef.current && value === lastRenderedValueRef.current) return;

    editor.innerHTML = markdownToHtml(value);
    initializedRef.current = true;
    lastRenderedValueRef.current = value;
    selectedCellRef.current = null;
    selectedTableCellsRef.current.clear();
    selectedTableRef.current = null;
    selectionAnchorCellRef.current = null;
    dragSelectionRef.current = null;
    resizeDragRef.current = null;
    editingCellRef.current = null;
    savedRangeRef.current = null;
    historyRef.current = [value];
    historyIndexRef.current = 0;
    updateHistoryState();
    setSelectedTableCellCount(0);
    setTableResizeOverlay(null);
    setTableFeedback("");
    setTableContext(emptyTableContext);
    setTableToolbarPanel(null);
  }, [value]);

  useEffect(() => {
    if (!tableToolbarPanel) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".manual-si-toolbar-popover-wrap")) return;
      setTableToolbarPanel(null);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [tableToolbarPanel]);

  useEffect(() => {
    if (!tableContext.active) setTableToolbarPanel(null);
  }, [tableContext.active]);

  const commitEditorChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const markdown = editorToMarkdown(editor);
    const current = historyRef.current[historyIndexRef.current] ?? null;
    if (markdown !== current) {
      const nextHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
      nextHistory.push(markdown);
      if (nextHistory.length > 120) nextHistory.shift();
      historyRef.current = nextHistory;
      historyIndexRef.current = nextHistory.length - 1;
      updateHistoryState();
    }
    lastRenderedValueRef.current = markdown;
    onChange(markdown);
  };

  const applyHistorySnapshot = (index: number) => {
    const editor = editorRef.current;
    const markdown = historyRef.current[index];
    if (!editor || markdown === undefined) return;
    editingCellRef.current?.classList.remove("is-editing-cell");
    editingCellRef.current = null;
    selectedCellRef.current = null;
    selectedTableCellsRef.current.forEach((cell) => cell.classList.remove("is-selected-range-cell"));
    selectedTableCellsRef.current.clear();
    selectedTableRef.current = null;
    selectionAnchorCellRef.current = null;
    dragSelectionRef.current = null;
    resizeDragRef.current = null;
    setTableResizeOverlay(null);
    editor.innerHTML = markdownToHtml(markdown);
    historyIndexRef.current = index;
    lastRenderedValueRef.current = markdown;
    setSelectedTableCellCount(0);
    setTableContext(emptyTableContext);
    setTableFeedback("");
    updateHistoryState();
    onChange(markdown);
  };

  const undoHistory = () => {
    if (disabled || historyIndexRef.current <= 0) return;
    applyHistorySnapshot(historyIndexRef.current - 1);
  };

  const redoHistory = () => {
    if (disabled || historyIndexRef.current >= historyRef.current.length - 1) return;
    applyHistorySnapshot(historyIndexRef.current + 1);
  };

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  };

  const findCellFromSelection = (): HTMLTableCellElement | null => {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    const element = node instanceof HTMLElement ? node : node?.parentElement;
    return (element?.closest("td, th") as HTMLTableCellElement | null) ?? null;
  };

  const clearTableSelection = () => {
    selectedTableCellsRef.current.forEach((cell) => cell.classList.remove("is-selected-range-cell"));
    selectedTableCellsRef.current.clear();
    selectedTableRef.current = null;
    selectionAnchorCellRef.current = null;
    setSelectedTableCellCount(0);
    if (tableColorScope === "selection") setTableColorScope("cell");
  };

  const addCellsToTableSelection = (table: HTMLTableElement, cells: HTMLTableCellElement[]) => {
    if (selectedTableRef.current && selectedTableRef.current !== table) clearTableSelection();
    selectedTableRef.current = table;
    cells.forEach((cell) => {
      if (!cell.isConnected) return;
      selectedTableCellsRef.current.add(cell);
      cell.classList.add("is-selected-range-cell");
    });
    setSelectedTableCellCount(selectedTableCellsRef.current.size);
    setTableColorScope("selection");
  };

  const selectedFormattingCells = (table: HTMLTableElement): HTMLTableCellElement[] => {
    const selected = Array.from(selectedTableCellsRef.current)
      .filter((cell) => cell.isConnected && cell.closest("table") === table);
    return selected;
  };

  const refreshTableResizeOverlay = (fallbackCell = selectedCellRef.current) => {
    const root = rootRef.current;
    if (!root || !fallbackCell || !fallbackCell.isConnected) {
      setTableResizeOverlay(null);
      return;
    }
    const table = fallbackCell.closest("table") as HTMLTableElement | null;
    if (!table) {
      setTableResizeOverlay(null);
      return;
    }
    const selected = selectedFormattingCells(table);
    const targets = (selected.length > 0 ? selected : [fallbackCell])
      .filter((cell) => cell.dataset.manualCovered !== "1");
    if (targets.length === 0) {
      setTableResizeOverlay(null);
      return;
    }
    const rootRect = root.getBoundingClientRect();
    const rects = targets.map((cell) => cell.getBoundingClientRect());
    const left = Math.min(...rects.map((rect) => rect.left)) - rootRect.left;
    const top = Math.min(...rects.map((rect) => rect.top)) - rootRect.top;
    const right = Math.max(...rects.map((rect) => rect.right)) - rootRect.left;
    const bottom = Math.max(...rects.map((rect) => rect.bottom)) - rootRect.top;
    setTableResizeOverlay({ left, top, width: right - left, height: bottom - top });
  };

  const cellFromEventTarget = (target: EventTarget | null): HTMLTableCellElement | null => {
    const element = target instanceof HTMLElement ? target : null;
    return (element?.closest("td, th") as HTMLTableCellElement | null) ?? null;
  };

  const tableRangeCells = (
    table: HTMLTableElement,
    startCell: HTMLTableCellElement,
    endCell: HTMLTableCellElement,
  ): HTMLTableCellElement[] => {
    const startRow = (startCell.parentElement as HTMLTableRowElement | null)?.rowIndex ?? 0;
    const endRow = (endCell.parentElement as HTMLTableRowElement | null)?.rowIndex ?? startRow;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minColumn = Math.min(startCell.cellIndex, endCell.cellIndex);
    const maxColumn = Math.max(startCell.cellIndex, endCell.cellIndex);
    const cells: HTMLTableCellElement[] = [];
    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
      for (let columnIndex = minColumn; columnIndex <= maxColumn; columnIndex += 1) {
        const cell = table.rows[rowIndex]?.cells[columnIndex];
        if (cell && cell.dataset.manualCovered !== "1") cells.push(cell);
      }
    }
    return cells;
  };

  const replaceTableSelection = (table: HTMLTableElement, cells: HTMLTableCellElement[]) => {
    clearTableSelection();
    addCellsToTableSelection(table, cells);
  };

  const selectSingleTableCell = (table: HTMLTableElement, cell: HTMLTableCellElement) => {
    replaceTableSelection(table, [cell]);
    selectionAnchorCellRef.current = cell;
    syncTableContext(cell);
  };

  const toggleTableCellSelection = (table: HTMLTableElement, cell: HTMLTableCellElement) => {
    if (selectedTableRef.current && selectedTableRef.current !== table) clearTableSelection();
    selectedTableRef.current = table;
    if (selectedTableCellsRef.current.has(cell)) {
      selectedTableCellsRef.current.delete(cell);
      cell.classList.remove("is-selected-range-cell");
    } else {
      selectedTableCellsRef.current.add(cell);
      cell.classList.add("is-selected-range-cell");
    }
    if (selectedTableCellsRef.current.size === 0) {
      selectedTableCellsRef.current.add(cell);
      cell.classList.add("is-selected-range-cell");
    }
    setSelectedTableCellCount(selectedTableCellsRef.current.size);
    setTableColorScope("selection");
    selectionAnchorCellRef.current = cell;
    syncTableContext(cell);
  };

  const leaveCellTextEditing = () => {
    editingCellRef.current?.classList.remove("is-editing-cell");
    editingCellRef.current = null;
  };

  const handleSurfaceMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    const cell = cellFromEventTarget(event.target);
    if (!cell || disabled) {
      if (!cell) {
        leaveCellTextEditing();
        if (selectedCellRef.current || selectedTableCellsRef.current.size > 0) syncTableContext(null);
      }
      return;
    }

    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table || event.button !== 0) return;

    if (event.detail >= 2 || editingCellRef.current === cell) {
      leaveCellTextEditing();
      editingCellRef.current = cell;
      cell.classList.add("is-editing-cell");
      if (!selectedTableCellsRef.current.has(cell)) selectSingleTableCell(table, cell);
      else syncTableContext(cell);
      return;
    }

    event.preventDefault();
    leaveCellTextEditing();
    window.getSelection()?.removeAllRanges();

    const anchor = selectionAnchorCellRef.current;
    if (event.shiftKey && anchor && anchor.closest("table") === table) {
      replaceTableSelection(table, tableRangeCells(table, anchor, cell));
      selectionAnchorCellRef.current = anchor;
      syncTableContext(anchor);
      dragSelectionRef.current = { table, startCell: anchor };
    } else if (event.ctrlKey || event.metaKey) {
      toggleTableCellSelection(table, cell);
      dragSelectionRef.current = null;
    } else {
      selectSingleTableCell(table, cell);
      dragSelectionRef.current = { table, startCell: cell };
    }

    editorRef.current?.focus({ preventScroll: true });
  };

  const handleSurfaceMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const drag = dragSelectionRef.current;
    if (!drag || (event.buttons & 1) !== 1) return;
    const cell = cellFromEventTarget(event.target);
    if (!cell || cell.closest("table") !== drag.table) return;
    event.preventDefault();
    replaceTableSelection(drag.table, tableRangeCells(drag.table, drag.startCell, cell));
    selectionAnchorCellRef.current = drag.startCell;
    syncTableContext(drag.startCell);
  };

  const handleSurfaceMouseUp = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!dragSelectionRef.current) return;
    event.preventDefault();
    dragSelectionRef.current = null;
    syncTableContext(selectedCellRef.current ?? undefined);
  };

  const handleSurfaceDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const cell = cellFromEventTarget(event.target);
    if (!cell || disabled) return;
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) return;
    leaveCellTextEditing();
    editingCellRef.current = cell;
    cell.classList.add("is-editing-cell");
    if (!selectedTableCellsRef.current.has(cell)) selectSingleTableCell(table, cell);
    else syncTableContext(cell);
    setTableFeedback("Edición de texto activa en la celda. Haz clic fuera para volver a seleccionar celdas.");
  };

  const handleSurfaceKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) redoHistory();
      else undoHistory();
      return;
    }
    if (modifier && event.key.toLowerCase() === "y") {
      event.preventDefault();
      redoHistory();
      return;
    }
    if (event.key === "Escape" && editingCellRef.current) {
      event.preventDefault();
      leaveCellTextEditing();
      window.getSelection()?.removeAllRanges();
      syncTableContext(selectedCellRef.current ?? undefined);
    }
  };

  const rowInsertionCrossesMerge = (table: HTMLTableElement, insertRowIndex: number): boolean =>
    Array.from(table.querySelectorAll("th, td")).some((candidate) => {
      if (!(candidate instanceof HTMLTableCellElement) || candidate.dataset.manualCovered === "1") return false;
      const row = candidate.parentElement as HTMLTableRowElement | null;
      const rowIndex = row?.rowIndex ?? -1;
      const rowSpan = Math.max(1, candidate.rowSpan || 1);
      return rowSpan > 1 && rowIndex < insertRowIndex && rowIndex + rowSpan > insertRowIndex;
    });

  const rowDeletionIntersectsMerge = (table: HTMLTableElement, rowIndex: number): boolean =>
    Array.from(table.querySelectorAll("th, td")).some((candidate) => {
      if (!(candidate instanceof HTMLTableCellElement) || candidate.dataset.manualCovered === "1") return false;
      const row = candidate.parentElement as HTMLTableRowElement | null;
      const anchorRow = row?.rowIndex ?? -1;
      const rowSpan = Math.max(1, candidate.rowSpan || 1);
      return rowSpan > 1 && anchorRow <= rowIndex && anchorRow + rowSpan > rowIndex;
    });

  const columnInsertionCrossesMerge = (table: HTMLTableElement, insertColumnIndex: number): boolean =>
    Array.from(table.querySelectorAll("th, td")).some((candidate) => {
      if (!(candidate instanceof HTMLTableCellElement) || candidate.dataset.manualCovered === "1") return false;
      const colSpan = Math.max(1, candidate.colSpan || 1);
      const anchorColumn = candidate.cellIndex;
      return colSpan > 1 && anchorColumn < insertColumnIndex && anchorColumn + colSpan > insertColumnIndex;
    });

  const columnDeletionIntersectsMerge = (table: HTMLTableElement, columnIndex: number): boolean =>
    Array.from(table.querySelectorAll("th, td")).some((candidate) => {
      if (!(candidate instanceof HTMLTableCellElement) || candidate.dataset.manualCovered === "1") return false;
      const colSpan = Math.max(1, candidate.colSpan || 1);
      const anchorColumn = candidate.cellIndex;
      return colSpan > 1 && anchorColumn <= columnIndex && anchorColumn + colSpan > columnIndex;
    });

  const syncTableContext = (cell = findCellFromSelection()) => {
    const editor = editorRef.current;
    if (!editor || !cell || !editor.contains(cell)) {
      selectedCellRef.current?.classList.remove("is-selected-cell");
      selectedCellRef.current = null;
      if (selectedTableCellsRef.current.size > 0) clearTableSelection();
      setTableContext(emptyTableContext);
      setTableResizeOverlay(null);
      return;
    }

    selectedCellRef.current?.classList.remove("is-selected-cell");
    selectedCellRef.current = cell;
    cell.classList.add("is-selected-cell");
    const table = cell.closest("table") as HTMLTableElement | null;
    if (table && selectedTableRef.current && selectedTableRef.current !== table) clearTableSelection();
    const row = cell.parentElement as HTMLTableRowElement | null;
    const isHeaderRow = row?.parentElement?.tagName === "THEAD";
    const explicitColor = cell.dataset.manualBg;
    const computedColor = window.getComputedStyle(cell).backgroundColor;
    const nextColor = explicitColor && isHexColor(explicitColor) ? explicitColor : cssColorToHex(computedColor);
    if (nextColor) setSelectedColor(nextColor);
    const explicitTextColor = cell.dataset.manualFg;
    const computedTextColor = cssColorToHex(window.getComputedStyle(cell).color);
    if (explicitTextColor && isHexColor(explicitTextColor)) setSelectedTextColor(explicitTextColor);
    else if (computedTextColor) setSelectedTextColor(computedTextColor);
    const explicitTextSize = Number(cell.dataset.manualFs ?? "");
    if (Number.isFinite(explicitTextSize) && explicitTextSize >= 8 && explicitTextSize <= 48) setSelectedTextSize(explicitTextSize);
    const explicitBorderColor = cell.dataset.manualBc;
    const computedBorderColor = cssColorToHex(window.getComputedStyle(cell).borderTopColor);
    if (explicitBorderColor && isHexColor(explicitBorderColor)) setSelectedBorderColor(explicitBorderColor);
    else if (computedBorderColor) setSelectedBorderColor(computedBorderColor);
    const explicitBorderWidth = cell.dataset.manualBw !== undefined ? Number(cell.dataset.manualBw) : Number.NaN;
    const computedBorderWidth = Math.max(0, Math.min(8, Math.round(parseFloat(window.getComputedStyle(cell).borderTopWidth) || 0)));
    if (Number.isFinite(explicitBorderWidth) && explicitBorderWidth >= 0 && explicitBorderWidth <= 8) setSelectedBorderWidth(explicitBorderWidth);
    else setSelectedBorderWidth(computedBorderWidth);
    const hasMergedCells = Boolean(table?.querySelector('td[rowspan], td[colspan], th[rowspan], th[colspan]'));
    const rowIndex = row?.rowIndex ?? -1;
    const columnIndex = cell.cellIndex;
    const canAddRow = Boolean(table && row && !rowInsertionCrossesMerge(table, rowIndex + 1));
    const canDeleteRow = Boolean(table && row && !isHeaderRow && !rowDeletionIntersectsMerge(table, rowIndex));
    const canAddColumn = Boolean(table && !columnInsertionCrossesMerge(table, columnIndex + 1));
    const canDeleteColumn = Boolean(
      table
      && table.rows[0]?.cells.length > 1
      && !columnDeletionIntersectsMerge(table, columnIndex),
    );
    setTableContext({
      active: Boolean(table),
      canAddRow,
      canDeleteRow,
      canAddColumn,
      canDeleteColumn,
      hasMergedCells,
      canUnmergeCell: cell.rowSpan > 1 || cell.colSpan > 1,
    });
    window.requestAnimationFrame(() => refreshTableResizeOverlay(cell));
  };

  const applyList = (ordered: boolean) => {
    if (disabled) return;
    const editor = editorRef.current;
    if (!editor) return;

    restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;

    const targetTag = ordered ? "OL" : "UL";
    const blockSelector = "p, div, h1, h2, h3, h4, h5, h6, li";
    const blockTags = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "LI"]);
    const candidates = Array.from(editor.querySelectorAll<HTMLElement>(blockSelector))
      .filter((block) => !block.closest("table") && range.intersectsNode(block));
    const units = candidates
      .filter((block) => {
        if (block.tagName === "LI") return true;
        return !Array.from(block.children).some((child) => blockTags.has(child.tagName) || child.tagName === "UL" || child.tagName === "OL");
      })
      .filter((block) => !block.closest("li") || block.tagName === "LI")
      .sort((left, right) => {
        if (left === right) return 0;
        return left.compareDocumentPosition(right) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });

    if (units.length === 0) {
      const selectedLines = selection.toString()
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (selectedLines.length === 0) return;

      const list = document.createElement(ordered ? "ol" : "ul");
      selectedLines.forEach((line) => {
        const item = document.createElement("li");
        item.textContent = line;
        list.appendChild(item);
      });
      range.deleteContents();
      range.insertNode(list);
      const after = document.createRange();
      after.selectNodeContents(list);
      selection.removeAllRanges();
      selection.addRange(after);
      savedRangeRef.current = after.cloneRange();
      syncTableContext(null);
      commitEditorChange();
      return;
    }

    const selectedTargetItems = units.filter((unit): unit is HTMLLIElement => {
      if (!(unit instanceof HTMLLIElement)) return false;
      const parentList = unit.parentElement;
      return parentList?.tagName === targetTag;
    });
    const selectedOtherListItems = units.filter((unit) => {
      if (!(unit instanceof HTMLLIElement)) return false;
      const parentList = unit.parentElement;
      return parentList?.tagName === (ordered ? "UL" : "OL");
    });

    // Toggle estable: si la selección ya contiene elementos del mismo tipo de lista
    // (aunque también haya párrafos normales), quitamos ese formato en lugar de
    // crear una lista anidada accidentalmente.
    if (selectedTargetItems.length > 0 && selectedOtherListItems.length === 0) {
      const byList = new Map<HTMLUListElement | HTMLOListElement, Set<HTMLLIElement>>();
      selectedTargetItems.forEach((item) => {
        const parentList = item.parentElement;
        if (!(parentList instanceof HTMLUListElement || parentList instanceof HTMLOListElement)) return;
        const selectedItems = byList.get(parentList) ?? new Set<HTMLLIElement>();
        selectedItems.add(item);
        byList.set(parentList, selectedItems);
      });

      const unwrapped: HTMLElement[] = [];
      byList.forEach((selectedItems, list) => {
        const fragment = document.createDocumentFragment();
        let pendingList: HTMLUListElement | HTMLOListElement | null = null;
        const flushPendingList = () => {
          if (pendingList && pendingList.children.length > 0) fragment.appendChild(pendingList);
          pendingList = null;
        };

        Array.from(list.children).forEach((child) => {
          if (!(child instanceof HTMLLIElement)) return;
          if (!selectedItems.has(child)) {
            if (!pendingList) pendingList = document.createElement(list.tagName.toLowerCase()) as HTMLUListElement | HTMLOListElement;
            pendingList.appendChild(child.cloneNode(true));
            return;
          }

          flushPendingList();
          const paragraph = document.createElement("p");
          Array.from(child.childNodes).forEach((node) => {
            if (node instanceof HTMLElement && (node.tagName === "UL" || node.tagName === "OL")) return;
            paragraph.appendChild(node.cloneNode(true));
          });
          fragment.appendChild(paragraph);
          unwrapped.push(paragraph);
          Array.from(child.children)
            .filter((nested) => nested.tagName === "UL" || nested.tagName === "OL")
            .forEach((nested) => fragment.appendChild(nested.cloneNode(true)));
        });
        flushPendingList();
        list.replaceWith(fragment);
      });

      if (unwrapped.length > 0) {
        const after = document.createRange();
        after.setStartBefore(unwrapped[0]);
        after.setEndAfter(unwrapped[unwrapped.length - 1]);
        selection.removeAllRanges();
        selection.addRange(after);
        savedRangeRef.current = after.cloneRange();
      }
      syncTableContext(null);
      commitEditorChange();
      return;
    }

    const list = document.createElement(ordered ? "ol" : "ul");
    const listParents = new Set<HTMLElement>();
    const sourceNodes: HTMLElement[] = [];

    units.forEach((unit) => {
      const item = document.createElement("li");
      if (unit instanceof HTMLLIElement) {
        const clone = unit.cloneNode(true) as HTMLLIElement;
        clone.querySelectorAll("ul, ol").forEach((nested) => nested.remove());
        item.innerHTML = clone.innerHTML;
        if (unit.parentElement) listParents.add(unit.parentElement);
      } else {
        item.innerHTML = unit.innerHTML;
      }
      if (item.textContent?.trim()) list.appendChild(item);
      sourceNodes.push(unit);
    });

    if (list.children.length === 0) return;

    const first = sourceNodes[0];
    let insertionPoint: HTMLElement = first;
    if (first instanceof HTMLLIElement && first.parentElement) insertionPoint = first.parentElement;
    insertionPoint.parentElement?.insertBefore(list, insertionPoint);

    sourceNodes.forEach((node) => node.remove());
    listParents.forEach((parentList) => {
      if ((parentList.tagName === "UL" || parentList.tagName === "OL") && parentList.children.length === 0) parentList.remove();
    });

    const after = document.createRange();
    after.selectNodeContents(list);
    selection.removeAllRanges();
    selection.addRange(after);
    savedRangeRef.current = after.cloneRange();
    syncTableContext(null);
    commitEditorChange();
  };

  const execute = (command: "bold" | "italic" | "insertUnorderedList" | "insertOrderedList") => {
    if (disabled) return;
    if (command === "insertUnorderedList" || command === "insertOrderedList") {
      applyList(command === "insertOrderedList");
      return;
    }
    const tableCell = selectedCellRef.current?.closest("table") ? selectedCellRef.current : null;
    if (tableCell) {
      const currentStyle = readCellStyle(tableCell);
      applyCellTextFormat(command === "bold"
        ? { bold: !(currentStyle.bold ?? false) }
        : { italic: !(currentStyle.italic ?? false) });
      return;
    }
    restoreSelection();
    document.execCommand(command);
    rememberSelection();
    syncTableContext(selectedCellRef.current ?? undefined);
    commitEditorChange();
  };

  const restoreSelection = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    if (savedRangeRef.current && editor.contains(savedRangeRef.current.commonAncestorContainer)) {
      selection.addRange(savedRangeRef.current);
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.addRange(range);
  };

  const insertTable = () => {
    if (disabled) return;
    const rows = Math.min(12, Math.max(2, Math.trunc(tableRows)));
    const columns = Math.min(8, Math.max(1, Math.trunc(tableColumns)));
    restoreSelection();

    const table = document.createElement("table");
    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    for (let column = 0; column < columns; column += 1) {
      const cell = document.createElement("th");
      cell.textContent = `Columna ${column + 1}`;
      headerRow.appendChild(cell);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    for (let rowIndex = 1; rowIndex < rows; rowIndex += 1) {
      const row = document.createElement("tr");
      for (let column = 0; column < columns; column += 1) {
        row.appendChild(document.createElement("td"));
      }
      tbody.appendChild(row);
    }
    table.appendChild(tbody);

    const paragraph = document.createElement("p");
    paragraph.appendChild(document.createElement("br"));

    const editor = editorRef.current;
    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode ?? null;
    let anchorElement = anchorNode instanceof HTMLElement ? anchorNode : anchorNode?.parentElement ?? null;
    while (anchorElement?.parentElement && anchorElement.parentElement !== editor) {
      anchorElement = anchorElement.parentElement;
    }

    if (editor && anchorElement?.parentElement === editor) {
      anchorElement.after(table, paragraph);
    } else {
      editor?.append(table, paragraph);
    }

    setTableBuilderOpen(false);
    commitEditorChange();
    window.requestAnimationFrame(() => {
      const firstCell = table.querySelector("th") as HTMLTableCellElement | null;
      if (!firstCell) return;
      const nextRange = document.createRange();
      nextRange.selectNodeContents(firstCell);
      const nextSelection = window.getSelection();
      nextSelection?.removeAllRanges();
      nextSelection?.addRange(nextRange);
      firstCell.focus();
      rememberSelection();
      syncTableContext(firstCell);
    });
  };

  const withSelectedTable = (callback: (table: HTMLTableElement, cell: HTMLTableCellElement) => void) => {
    const cell = selectedCellRef.current;
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!cell || !table || disabled) return;
    callback(table, cell);
    syncIndependentWidthMode(table);
    const connectedSelection = new Set(
      Array.from(selectedTableCellsRef.current).filter((selectedCell) => selectedCell.isConnected && selectedCell.closest("table") === table),
    );
    selectedTableCellsRef.current = connectedSelection;
    setSelectedTableCellCount(connectedSelection.size);
    commitEditorChange();
    const nextCell = selectedCellRef.current;
    syncTableContext(nextCell?.isConnected ? nextCell : null);
  };

  const addTableRow = () => withSelectedTable((table, cell) => {
    const selectedRow = cell.parentElement as HTMLTableRowElement;
    const insertRowIndex = selectedRow.rowIndex + 1;
    if (rowInsertionCrossesMerge(table, insertRowIndex)) {
      setTableFeedback("No se puede añadir una fila dentro de una combinación vertical. Selecciona la última fila de la combinación o sepárala primero.");
      return;
    }
    const tbody = table.tBodies[0] ?? table.createTBody();
    const bodyRows = Array.from(tbody.rows);
    const selectedBodyIndex = bodyRows.indexOf(selectedRow);
    const insertIndex = selectedBodyIndex >= 0 ? selectedBodyIndex + 1 : 0;
    const row = tbody.insertRow(insertIndex);
    const columnCount = table.rows[0]?.cells.length ?? 1;
    for (let column = 0; column < columnCount; column += 1) row.insertCell();
    selectedCellRef.current = row.cells[Math.min(cell.cellIndex, row.cells.length - 1)] ?? cell;
    setTableFeedback("Fila añadida.");
  });

  const deleteTableRow = () => withSelectedTable((table, cell) => {
    const row = cell.parentElement as HTMLTableRowElement;
    if (row.parentElement?.tagName === "THEAD") return;
    if (rowDeletionIntersectsMerge(table, row.rowIndex)) {
      setTableFeedback("No se puede quitar una fila que forma parte de una combinación vertical. Separa primero esas celdas.");
      return;
    }
    const nextCell = row.nextElementSibling?.children[cell.cellIndex]
      ?? row.previousElementSibling?.children[cell.cellIndex]
      ?? row.closest("table")?.querySelector(`thead th:nth-child(${cell.cellIndex + 1})`);
    row.remove();
    selectedCellRef.current = nextCell instanceof HTMLTableCellElement ? nextCell : null;
    setTableFeedback("Fila eliminada.");
  });

  const addTableColumn = () => withSelectedTable((table, cell) => {
    const targetIndex = cell.cellIndex + 1;
    if (columnInsertionCrossesMerge(table, targetIndex)) {
      setTableFeedback("No se puede añadir una columna dentro de una combinación horizontal. Selecciona la última columna de la combinación o sepárala primero.");
      return;
    }
    Array.from(table.rows).forEach((row, rowIndex) => {
      const newCell = document.createElement(rowIndex === 0 ? "th" : "td");
      if (rowIndex === 0) newCell.textContent = `Columna ${targetIndex + 1}`;
      row.insertBefore(newCell, row.cells[targetIndex] ?? null);
    });
    setTableFeedback("Columna añadida.");
  });

  const deleteTableColumn = () => withSelectedTable((table, cell) => {
    const columnCount = table.rows[0]?.cells.length ?? 0;
    if (columnCount <= 1) return;
    const targetIndex = cell.cellIndex;
    if (columnDeletionIntersectsMerge(table, targetIndex)) {
      setTableFeedback("No se puede quitar una columna que forma parte de una combinación horizontal. Separa primero esas celdas.");
      return;
    }
    const rowIndex = cell.parentElement instanceof HTMLTableRowElement ? cell.parentElement.rowIndex : 0;
    Array.from(table.rows).forEach((row) => row.cells[targetIndex]?.remove());
    const nextIndex = Math.min(targetIndex, columnCount - 2);
    selectedCellRef.current = table.rows[Math.min(rowIndex, table.rows.length - 1)]?.cells[nextIndex] ?? null;
    setTableFeedback("Columna eliminada.");
  });

  const deleteTable = () => withSelectedTable((table) => {
    const paragraph = document.createElement("p");
    paragraph.appendChild(document.createElement("br"));
    table.replaceWith(paragraph);
    selectedCellRef.current = null;
    clearTableSelection();
    setTableContext(emptyTableContext);
  });

  const tableColorTargets = (table: HTMLTableElement, cell: HTMLTableCellElement): HTMLTableCellElement[] => {
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row) return [cell];

    switch (tableColorScope) {
      case "selection": {
        const selected = selectedFormattingCells(table);
        return selected.length > 0 ? selected : [cell];
      }
      case "row":
        return Array.from(row.cells);
      case "column":
        return Array.from(table.rows)
          .map((tableRow) => tableRow.cells[cell.cellIndex] ?? null)
          .filter((target): target is HTMLTableCellElement => target !== null);
      case "table":
        return Array.from(table.rows).flatMap((tableRow) => Array.from(tableRow.cells));
      default:
        return [cell];
    }
  };

  const currentTableSelection = (): { table: HTMLTableElement; cell: HTMLTableCellElement } | null => {
    const cell = selectedCellRef.current;
    const table = cell?.closest("table") as HTMLTableElement | null;
    return cell && table && !disabled ? { table, cell } : null;
  };

  const selectCurrentRow = () => {
    const current = currentTableSelection();
    if (!current) return;
    const row = current.cell.parentElement as HTMLTableRowElement | null;
    if (!row) return;
    addCellsToTableSelection(current.table, Array.from(row.cells).filter((cell) => cell.dataset.manualCovered !== "1"));
    selectionAnchorCellRef.current = current.cell;
    syncTableContext(current.cell);
  };

  const selectCurrentColumn = () => {
    const current = currentTableSelection();
    if (!current) return;
    addCellsToTableSelection(
      current.table,
      Array.from(current.table.rows)
        .map((row) => row.cells[current.cell.cellIndex] ?? null)
        .filter((target): target is HTMLTableCellElement => target !== null && target.dataset.manualCovered !== "1"),
    );
    selectionAnchorCellRef.current = current.cell;
    syncTableContext(current.cell);
  };

  const selectEntireTable = () => {
    const current = currentTableSelection();
    if (!current) return;
    addCellsToTableSelection(
      current.table,
      Array.from(current.table.rows).flatMap((row) => Array.from(row.cells)).filter((cell) => cell.dataset.manualCovered !== "1"),
    );
    selectionAnchorCellRef.current = current.cell;
    syncTableContext(current.cell);
  };

  const clearManualCellFormat = (cell: HTMLTableCellElement, keepMerge = true, keepDimensions = false) => {
    delete cell.dataset.manualBg;
    delete cell.dataset.manualFg;
    delete cell.dataset.manualFs;
    delete cell.dataset.manualBold;
    delete cell.dataset.manualItalic;
    delete cell.dataset.manualAlign;
    delete cell.dataset.manualBc;
    delete cell.dataset.manualBw;
    if (!keepDimensions) {
      delete cell.dataset.manualW;
      delete cell.dataset.manualH;
    }
    cell.style.removeProperty("background-color");
    cell.style.removeProperty("color");
    cell.style.removeProperty("font-size");
    cell.style.removeProperty("font-weight");
    cell.style.removeProperty("font-style");
    cell.style.removeProperty("text-align");
    cell.style.removeProperty("border");
    if (!keepDimensions) {
      cell.style.removeProperty("width");
      cell.style.removeProperty("min-width");
      cell.style.removeProperty("height");
    }
    if (!keepMerge) {
      delete cell.dataset.manualRs;
      delete cell.dataset.manualCs;
      cell.rowSpan = 1;
      cell.colSpan = 1;
    }
  };

  const selectedRectangle = (table: HTMLTableElement): { cells: HTMLTableCellElement[]; minRow: number; maxRow: number; minColumn: number; maxColumn: number } | null => {
    const selected = selectedFormattingCells(table).filter((cell) => cell.dataset.manualCovered !== "1");
    if (selected.length < 2) return null;
    const coordinates = selected.map((cell) => ({ cell, row: (cell.parentElement as HTMLTableRowElement).rowIndex, column: cell.cellIndex }));
    const minRow = Math.min(...coordinates.map((item) => item.row));
    const maxRow = Math.max(...coordinates.map((item) => item.row));
    const minColumn = Math.min(...coordinates.map((item) => item.column));
    const maxColumn = Math.max(...coordinates.map((item) => item.column));
    const expectedCount = (maxRow - minRow + 1) * (maxColumn - minColumn + 1);
    if (selected.length !== expectedCount) return null;

    const headerMembership = coordinates.map((item) => item.cell.parentElement?.parentElement?.tagName === "THEAD");
    if (headerMembership.some(Boolean) && headerMembership.some((value) => !value)) return null;

    const selectedSet = new Set(selected);
    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
      for (let columnIndex = minColumn; columnIndex <= maxColumn; columnIndex += 1) {
        const target = table.rows[rowIndex]?.cells[columnIndex];
        if (!target || !selectedSet.has(target) || target.rowSpan > 1 || target.colSpan > 1 || target.dataset.manualCovered === "1") return null;
      }
    }
    const orderedCells = coordinates
      .sort((left, right) => left.row - right.row || left.column - right.column)
      .map((item) => item.cell);
    return { cells: orderedCells, minRow, maxRow, minColumn, maxColumn };
  };

  const mergeSelectedCells = () => withSelectedTable((table) => {
    const rectangle = selectedRectangle(table);
    if (!rectangle) {
      setTableFeedback("Selecciona al menos dos celdas contiguas formando un rectángulo dentro de la misma zona de la tabla.");
      return;
    }

    const anchor = table.rows[rectangle.minRow].cells[rectangle.minColumn];
    const contents = rectangle.cells
      .map((cell) => cell.innerHTML.trim())
      .filter(Boolean);
    anchor.innerHTML = contents.join("<br>");
    const rowSpan = rectangle.maxRow - rectangle.minRow + 1;
    const colSpan = rectangle.maxColumn - rectangle.minColumn + 1;
    anchor.rowSpan = rowSpan;
    anchor.colSpan = colSpan;
    if (rowSpan > 1) anchor.dataset.manualRs = String(rowSpan);
    if (colSpan > 1) anchor.dataset.manualCs = String(colSpan);

    rectangle.cells.forEach((cell) => {
      if (cell === anchor) return;
      cell.innerHTML = "";
      clearManualCellFormat(cell, false);
      cell.dataset.manualCovered = "1";
      cell.style.display = "none";
    });

    clearTableSelection();
    selectedCellRef.current = anchor;
    setTableFeedback("Celdas combinadas. El contenido se ha conservado en la celda resultante.");
  });

  const unmergeCurrentCell = () => withSelectedTable((table, cell) => {
    const rowSpan = Math.max(1, cell.rowSpan || 1);
    const colSpan = Math.max(1, cell.colSpan || 1);
    if (rowSpan === 1 && colSpan === 1) {
      setTableFeedback("La celda activa no está combinada.");
      return;
    }
    const rowIndex = (cell.parentElement as HTMLTableRowElement).rowIndex;
    const columnIndex = cell.cellIndex;
    cell.rowSpan = 1;
    cell.colSpan = 1;
    delete cell.dataset.manualRs;
    delete cell.dataset.manualCs;
    const restored: HTMLTableCellElement[] = [cell];
    for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
      for (let columnOffset = 0; columnOffset < colSpan; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        const target = table.rows[rowIndex + rowOffset]?.cells[columnIndex + columnOffset];
        if (!target) continue;
        delete target.dataset.manualCovered;
        target.removeAttribute("aria-hidden");
        target.style.removeProperty("display");
        restored.push(target);
      }
    }
    clearTableSelection();
    addCellsToTableSelection(table, restored);
    selectedCellRef.current = cell;
    setTableFeedback("Celdas separadas. La celda principal conserva el contenido combinado.");
  });

  const readCellStyle = (cell: HTMLTableCellElement): TableCellFormat => {
    const computed = window.getComputedStyle(cell);
    const firstTextNode = Array.from(cell.childNodes).reduce<Text | null>((found, node) => {
      if (found) return found;
      if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim()) return node as Text;
      if (!(node instanceof HTMLElement)) return null;
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      let current = walker.nextNode();
      while (current) {
        if ((current.textContent ?? "").trim()) return current as Text;
        current = walker.nextNode();
      }
      return null;
    }, null);
    const textElement = firstTextNode?.parentElement && cell.contains(firstTextNode.parentElement)
      ? firstTextNode.parentElement
      : cell;
    const textComputed = window.getComputedStyle(textElement);
    const explicitFontSize = Number(cell.dataset.manualFs ?? "");
    const computedFontSize = Math.round((parseFloat(textComputed.fontSize) || 12) * 0.75);
    const explicitBorderWidth = cell.dataset.manualBw !== undefined ? Number(cell.dataset.manualBw) : Number.NaN;
    const computedBorderWidth = Math.max(0, Math.min(8, Math.round(parseFloat(computed.borderTopWidth) || 0)));
    const explicitBold = cell.dataset.manualBold;
    const explicitItalic = cell.dataset.manualItalic;
    const computedWeight = Number.parseInt(textComputed.fontWeight, 10);
    const computedBold = Number.isFinite(computedWeight) ? computedWeight >= 600 : textComputed.fontWeight === "bold";
    const computedItalic = textComputed.fontStyle === "italic" || textComputed.fontStyle === "oblique";
    return {
      background: cell.dataset.manualBg && isHexColor(cell.dataset.manualBg)
        ? cell.dataset.manualBg.toLowerCase()
        : cssColorToHex(computed.backgroundColor) ?? undefined,
      color: cell.dataset.manualFg && isHexColor(cell.dataset.manualFg)
        ? cell.dataset.manualFg.toLowerCase()
        : cssColorToHex(textComputed.color) ?? undefined,
      fontSize: Number.isFinite(explicitFontSize) && explicitFontSize >= 8 && explicitFontSize <= 48
        ? explicitFontSize
        : Math.max(8, Math.min(48, computedFontSize)),
      bold: explicitBold === "0" || explicitBold === "1" ? explicitBold === "1" : computedBold,
      italic: explicitItalic === "0" || explicitItalic === "1" ? explicitItalic === "1" : computedItalic,
      textAlign: cell.dataset.manualAlign === "center" || cell.dataset.manualAlign === "right"
        ? cell.dataset.manualAlign
        : computed.textAlign === "center" || computed.textAlign === "right"
          ? computed.textAlign
          : "left",
      borderColor: cell.dataset.manualBc && isHexColor(cell.dataset.manualBc)
        ? cell.dataset.manualBc.toLowerCase()
        : cssColorToHex(computed.borderTopColor) ?? undefined,
      borderWidth: Number.isFinite(explicitBorderWidth) && explicitBorderWidth >= 0 && explicitBorderWidth <= 8
        ? explicitBorderWidth
        : computedBorderWidth,
    };
  };

  const normalizeCellEmphasisForStylePaste = (cell: HTMLTableCellElement, format: TableCellFormat) => {
    if (format.bold === false) {
      cell.querySelectorAll<HTMLElement>("strong, b").forEach((element) => element.replaceWith(...Array.from(element.childNodes)));
      cell.querySelectorAll<HTMLElement>("[style]").forEach((element) => element.style.removeProperty("font-weight"));
    }
    if (format.italic === false) {
      cell.querySelectorAll<HTMLElement>("em, i").forEach((element) => element.replaceWith(...Array.from(element.childNodes)));
      cell.querySelectorAll<HTMLElement>("[style]").forEach((element) => element.style.removeProperty("font-style"));
    }
  };

  const applyCellStyle = (cell: HTMLTableCellElement, format: TableCellFormat) => {
    clearManualCellFormat(cell, true, true);
    if (format.background && isHexColor(format.background)) {
      cell.dataset.manualBg = format.background.toLowerCase();
      cell.style.backgroundColor = format.background.toLowerCase();
    }
    if (format.color && isHexColor(format.color)) {
      cell.dataset.manualFg = format.color.toLowerCase();
      cell.style.color = format.color.toLowerCase();
    }
    if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) {
      cell.dataset.manualFs = String(format.fontSize);
      cell.style.fontSize = `${format.fontSize}pt`;
    }
    if (format.bold !== undefined) {
      cell.dataset.manualBold = format.bold ? "1" : "0";
      cell.style.fontWeight = format.bold ? "700" : "400";
    }
    if (format.italic !== undefined) {
      cell.dataset.manualItalic = format.italic ? "1" : "0";
      cell.style.fontStyle = format.italic ? "italic" : "normal";
    }
    if (format.textAlign) {
      cell.dataset.manualAlign = format.textAlign;
      cell.style.textAlign = format.textAlign;
    }
    const borderWidth = format.borderWidth;
    if (borderWidth !== undefined && borderWidth >= 0 && borderWidth <= 8) {
      cell.dataset.manualBw = String(borderWidth);
      if (format.borderColor && isHexColor(format.borderColor)) cell.dataset.manualBc = format.borderColor.toLowerCase();
      cell.style.border = borderWidth === 0
        ? "0"
        : `${borderWidth}px solid ${format.borderColor && isHexColor(format.borderColor) ? format.borderColor.toLowerCase() : "#cfdcd7"}`;
    }
  };

  const copyCellStyle = () => {
    const current = currentTableSelection();
    if (!current) {
      setTableFeedback("Selecciona primero una celda de la tabla.");
      return;
    }
    const style = readCellStyle(current.cell);
    copiedCellStyleRef.current = {
      background: style.background,
      color: style.color,
      fontSize: style.fontSize,
      bold: style.bold,
      italic: style.italic,
      textAlign: style.textAlign,
      borderColor: style.borderColor,
      borderWidth: style.borderWidth,
    };
    setHasCopiedCellStyle(true);
    setTableFeedback("Estilo copiado de la celda activa: fondo, color y tamaño de texto, negrita, cursiva, alineación y borde.");
  };

  const pasteCellStyle = () => {
    const current = currentTableSelection();
    const copied = copiedCellStyleRef.current;
    if (!current || !copied) return;
    const selected = selectedFormattingCells(current.table);
    const targets = selected.length > 0 ? selected : [current.cell];
    targets.forEach((target) => {
      if (target.dataset.manualCovered === "1") return;
      normalizeCellEmphasisForStylePaste(target, copied);
      applyCellStyle(target, copied);
    });
    setTableFeedback(`Estilo pegado en ${targets.length} ${targets.length === 1 ? "celda" : "celdas"}.`);
    commitEditorChange();
    syncTableContext(current.cell);
  };

  const selectedRowsForSizing = (table: HTMLTableElement, cell: HTMLTableCellElement): HTMLTableRowElement[] => {
    const selected = selectedFormattingCells(table);
    const source = selected.length > 0 ? selected : [cell];
    return Array.from(new Set(source
      .map((target) => target.parentElement)
      .filter((row): row is HTMLTableRowElement => row instanceof HTMLTableRowElement)));
  };

  const selectedColumnsForSizing = (table: HTMLTableElement, cell: HTMLTableCellElement): number[] => {
    const selected = selectedFormattingCells(table);
    const source = selected.length > 0 ? selected : [cell];
    const columns = new Set<number>();
    source.forEach((target) => {
      const span = Math.max(1, target.colSpan || 1);
      for (let offset = 0; offset < span; offset += 1) columns.add(target.cellIndex + offset);
    });
    return Array.from(columns).sort((left, right) => left - right);
  };

  const selectedCellsForWidth = (table: HTMLTableElement, cell: HTMLTableCellElement): {
    targets: HTMLTableCellElement[];
    columns: number[];
    fullColumns: boolean;
  } => {
    const selected = selectedFormattingCells(table).filter((target) => target.dataset.manualCovered !== "1");
    const source = selected.length > 0 ? selected : [cell];
    const selectedSet = new Set(source);
    const columns = selectedColumnsForSizing(table, cell);
    const fullColumns = columns.length > 0 && columns.every((columnIndex) => {
      const columnCells = Array.from(table.rows)
        .map((row) => row.cells[columnIndex] ?? null)
        .filter((target): target is HTMLTableCellElement => target !== null && target.dataset.manualCovered !== "1");
      return columnCells.length > 0 && columnCells.every((target) => selectedSet.has(target));
    });

    if (!fullColumns) return { targets: source, columns, fullColumns: false };

    const targets = Array.from(table.rows).flatMap((row) => columns
      .map((columnIndex) => row.cells[columnIndex] ?? null)
      .filter((target): target is HTMLTableCellElement => target !== null && target.dataset.manualCovered !== "1"));
    return { targets, columns, fullColumns: true };
  };

  const syncIndependentWidthMode = (table: HTMLTableElement) => {
    const visibleCells = Array.from(table.querySelectorAll<HTMLTableCellElement>("th, td"))
      .filter((target) => target.dataset.manualCovered !== "1");
    const hasMerge = visibleCells.some((target) => Math.max(1, target.rowSpan || 1) > 1 || Math.max(1, target.colSpan || 1) > 1);
    if (hasMerge) {
      delete table.dataset.manualFreeWidth;
      return;
    }

    const columnCount = Math.max(0, ...Array.from(table.rows).map((row) => row.cells.length));
    let independent = false;
    for (let columnIndex = 0; columnIndex < columnCount && !independent; columnIndex += 1) {
      const columnCells = Array.from(table.rows)
        .map((row) => row.cells[columnIndex] ?? null)
        .filter((target): target is HTMLTableCellElement => target !== null && target.dataset.manualCovered !== "1");
      const widths = columnCells.map((target) => {
        const width = Number(target.dataset.manualW ?? "");
        return Number.isFinite(width) && width >= 40 && width <= 1200 ? width : null;
      });
      const defined = widths.filter((width): width is number => width !== null);
      independent = defined.length > 0 && (defined.length !== columnCells.length || new Set(defined).size > 1);
    }

    if (independent) table.dataset.manualFreeWidth = "1";
    else delete table.dataset.manualFreeWidth;
  };

  const applyColumnWidth = (reset = false) => withSelectedTable((table, cell) => {
    const widthSelection = selectedCellsForWidth(table, cell);
    const width = Math.max(40, Math.min(1200, Math.round(selectedColumnWidth)));
    widthSelection.targets.forEach((target) => {
      if (reset) {
        delete target.dataset.manualW;
        target.style.removeProperty("width");
        target.style.removeProperty("min-width");
        return;
      }
      target.dataset.manualW = String(width);
      target.style.width = `${width}px`;
      target.style.minWidth = `${width}px`;
    });
    syncIndependentWidthMode(table);
    refreshTableResizeOverlay(cell);
    const amount = widthSelection.fullColumns ? widthSelection.columns.length : widthSelection.targets.length;
    const unit = widthSelection.fullColumns ? (amount === 1 ? "columna" : "columnas") : (amount === 1 ? "celda" : "celdas");
    setTableFeedback(reset
      ? `Ancho automático restaurado en ${amount} ${unit}.`
      : `Ancho de ${width}px aplicado a ${amount} ${unit}.`);
  });

  const applyRowHeight = (reset = false) => withSelectedTable((table, cell) => {
    const rows = selectedRowsForSizing(table, cell);
    const height = Math.max(24, Math.min(600, Math.round(selectedRowHeight)));
    rows.forEach((row) => {
      Array.from(row.cells).forEach((target) => {
        if (target.dataset.manualCovered === "1") return;
        if (reset) {
          delete target.dataset.manualH;
          target.style.removeProperty("height");
          return;
        }
        target.dataset.manualH = String(height);
        target.style.height = `${height}px`;
      });
    });
    setTableFeedback(reset
      ? `Altura automática restaurada en ${rows.length} fila${rows.length === 1 ? "" : "s"}.`
      : `Altura de ${height}px aplicada a ${rows.length} fila${rows.length === 1 ? "" : "s"}.`);
  });

  const applyCellTextAlignment = (alignment: TableTextAlign) => withSelectedTable((table, cell) => {
    const selected = selectedFormattingCells(table);
    const targets = selected.length > 0 ? selected : [cell];
    targets.forEach((target) => {
      if (target.dataset.manualCovered === "1") return;
      target.dataset.manualAlign = alignment;
      target.style.textAlign = alignment;
    });
    setTableFeedback(`Alineación ${alignment === "center" ? "centrada" : alignment === "right" ? "a la derecha" : "a la izquierda"} aplicada a ${targets.length} ${targets.length === 1 ? "celda" : "celdas"}.`);
  });

  const beginTableResize = (axis: "width" | "height", event: ReactMouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const current = currentTableSelection();
    if (!current) return;
    event.preventDefault();
    event.stopPropagation();
    leaveCellTextEditing();
    resizeDragRef.current = { axis };

    const startX = event.clientX;
    const startY = event.clientY;
    const widthSelection = selectedCellsForWidth(current.table, current.cell);
    const rows = selectedRowsForSizing(current.table, current.cell);
    const cellWidths = new Map<HTMLTableCellElement, number>();
    widthSelection.targets.forEach((target) => cellWidths.set(target, target.getBoundingClientRect().width));
    const rowHeights = new Map<HTMLTableRowElement, number>();
    rows.forEach((row) => rowHeights.set(row, row.getBoundingClientRect().height));

    const onMove = (moveEvent: MouseEvent) => {
      if (!resizeDragRef.current) return;
      if (axis === "width") {
        const delta = moveEvent.clientX - startX;
        let latestWidth = selectedColumnWidth;
        widthSelection.targets.forEach((target) => {
          const initial = cellWidths.get(target) ?? 100;
          const width = Math.max(40, Math.min(1200, Math.round(initial + delta)));
          target.dataset.manualW = String(width);
          target.style.width = `${width}px`;
          target.style.minWidth = `${width}px`;
          latestWidth = width;
        });
        setSelectedColumnWidth(latestWidth);
        syncIndependentWidthMode(current.table);
      } else {
        const delta = moveEvent.clientY - startY;
        rows.forEach((row) => {
          const initial = rowHeights.get(row) ?? 44;
          const height = Math.max(24, Math.min(600, Math.round(initial + delta)));
          Array.from(row.cells).forEach((target) => {
            if (target.dataset.manualCovered === "1") return;
            target.dataset.manualH = String(height);
            target.style.height = `${height}px`;
          });
          setSelectedRowHeight(height);
        });
      }
      refreshTableResizeOverlay(current.cell);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      resizeDragRef.current = null;
      commitEditorChange();
      syncTableContext(current.cell);
      const widthAmount = widthSelection.fullColumns ? widthSelection.columns.length : widthSelection.targets.length;
      const widthUnit = widthSelection.fullColumns
        ? (widthAmount === 1 ? "columna" : "columnas")
        : (widthAmount === 1 ? "celda" : "celdas");
      setTableFeedback(axis === "width"
        ? `Ancho ajustado arrastrando el borde en ${widthAmount} ${widthUnit}.`
        : `Altura ajustada arrastrando el borde en ${rows.length} fila${rows.length === 1 ? "" : "s"}.`);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const applyTableBorder = (remove = false) => withSelectedTable((table, cell) => {
    tableColorTargets(table, cell).forEach((target) => {
      if (target.dataset.manualCovered === "1") return;
      if (remove) {
        delete target.dataset.manualBc;
        target.dataset.manualBw = "0";
        target.style.border = "0";
        return;
      }
      const color = selectedBorderColor.toLowerCase();
      const width = Math.max(1, Math.min(8, Math.trunc(selectedBorderWidth)));
      if (!isHexColor(color)) return;
      target.dataset.manualBc = color;
      target.dataset.manualBw = String(width);
      target.style.border = `${width}px solid ${color}`;
    });
  });

  const applyTextFormatToCells = (
    cells: HTMLTableCellElement[],
    format: Pick<TableCellFormat, "color" | "fontSize" | "bold" | "italic">,
  ) => {
    cells.forEach((target) => {
      if (!target.isConnected || target.dataset.manualCovered === "1") return;
      if (format.color && isHexColor(format.color)) {
        const normalized = format.color.toLowerCase();
        target.dataset.manualFg = normalized;
        target.style.color = normalized;
        target.querySelectorAll<HTMLElement>('[data-manual-inline="1"][data-manual-fg]').forEach((inline) => {
          delete inline.dataset.manualFg;
          inline.style.removeProperty("color");
        });
      }
      if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) {
        target.dataset.manualFs = String(format.fontSize);
        target.style.fontSize = `${format.fontSize}pt`;
      }
      if (format.bold !== undefined) {
        target.dataset.manualBold = format.bold ? "1" : "0";
        target.style.fontWeight = format.bold ? "700" : "400";
      }
      if (format.italic !== undefined) {
        target.dataset.manualItalic = format.italic ? "1" : "0";
        target.style.fontStyle = format.italic ? "italic" : "normal";
      }
    });
  };

  const applyCellTextFormat = (format: Pick<TableCellFormat, "color" | "fontSize" | "bold" | "italic">) => withSelectedTable((table, cell) => {
    const targets = selectedFormattingCells(table);
    applyTextFormatToCells(targets.length > 0 ? targets : [cell], format);
  });

  const wrapSelectedText = (format: InlineTextFormat): boolean => {
    const editor = editorRef.current;
    if (!editor || disabled) return false;
    restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    if (range.collapsed || !editor.contains(range.commonAncestorContainer)) return false;
    const originalStartContainer = range.startContainer;
    const originalStartOffset = range.startOffset;
    const originalEndContainer = range.endContainer;
    const originalEndOffset = range.endOffset;

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
      if (current instanceof Text && current.data.length > 0) {
        try {
          if (range.intersectsNode(current)) textNodes.push(current);
        } catch {
          // Un nodo no seleccionable se ignora sin alterar el contenido.
        }
      }
      current = walker.nextNode();
    }

    [...textNodes].reverse().forEach((textNode) => {
      let start = textNode === originalStartContainer ? originalStartOffset : 0;
      let end = textNode === originalEndContainer ? originalEndOffset : textNode.data.length;
      start = Math.max(0, Math.min(start, textNode.data.length));
      end = Math.max(start, Math.min(end, textNode.data.length));
      if (start === end) return;

      let selectedNode = textNode;
      if (end < selectedNode.data.length) selectedNode.splitText(end);
      if (start > 0) selectedNode = selectedNode.splitText(start);

      const span = document.createElement("span");
      span.dataset.manualInline = "1";
      if (format.color && isHexColor(format.color)) {
        span.dataset.manualFg = format.color.toLowerCase();
        span.style.color = format.color.toLowerCase();
      }
      if (format.fontSize && format.fontSize >= 8 && format.fontSize <= 48) {
        span.dataset.manualFs = String(format.fontSize);
        span.style.fontSize = `${format.fontSize}pt`;
      }
      selectedNode.replaceWith(span);
      span.appendChild(selectedNode);
    });

    commitEditorChange();
    return textNodes.length > 0;
  };

  const captureTextColorTarget = () => {
    const current = currentTableSelection();
    if (current) {
      const selected = selectedFormattingCells(current.table);
      textColorTargetCellsRef.current = [...(selected.length > 0 ? selected : [current.cell])];
      textColorTargetRangeRef.current = null;
      return;
    }

    textColorTargetCellsRef.current = [];
    rememberSelection();
    const editor = editorRef.current;
    const savedRange = savedRangeRef.current;
    textColorTargetRangeRef.current = editor && savedRange && editor.contains(savedRange.commonAncestorContainer)
      ? savedRange.cloneRange()
      : null;
  };

  const applyTextColor = (color: string, commit = true) => {
    const normalized = color.toLowerCase();
    if (!isHexColor(normalized) || disabled) return;
    setSelectedTextColor(normalized);

    const capturedCells = textColorTargetCellsRef.current.filter((cell) => cell.isConnected);
    if (capturedCells.length > 0) {
      applyTextFormatToCells(capturedCells, { color: normalized });
      if (commit) {
        commitEditorChange();
        const active = selectedCellRef.current;
        syncTableContext(active?.isConnected ? active : capturedCells[0]);
        setTableFeedback(`Color de texto aplicado a ${capturedCells.length} ${capturedCells.length === 1 ? "celda" : "celdas"}.`);
      }
      return;
    }

    const capturedRange = textColorTargetRangeRef.current;
    const editor = editorRef.current;
    if (capturedRange && editor && editor.contains(capturedRange.commonAncestorContainer)) {
      savedRangeRef.current = capturedRange.cloneRange();
    }
    if (commit) wrapSelectedText({ color: normalized });
  };

  const applyTextSize = (size: number) => {
    if (!Number.isFinite(size) || size < 8 || size > 48 || disabled) return;
    setSelectedTextSize(size);
    if (currentTableSelection()) {
      applyCellTextFormat({ fontSize: size });
      return;
    }
    wrapSelectedText({ fontSize: size });
  };

  const applyTableColor = (remove = false) => withSelectedTable((table, cell) => {
    tableColorTargets(table, cell).forEach((target) => {
      if (remove) {
        delete target.dataset.manualBg;
        target.style.removeProperty("background-color");
        return;
      }
      const color = selectedColor.toLowerCase();
      if (!isHexColor(color)) return;
      target.dataset.manualBg = color;
      target.style.backgroundColor = color;
    });
  });

  return (
    <div ref={rootRef} className={`manual-si-visual-editor ${disabled ? "is-disabled" : ""}`}>
      <div className="manual-si-visual-editor-toolbar" aria-label="Herramientas de edición">
        <div className="manual-si-visual-editor-toolgroup">
          <ToolbarButton label="Negrita" disabled={disabled} onClick={() => execute("bold")}><Bold size={16} /></ToolbarButton>
          <ToolbarButton label="Cursiva" disabled={disabled} onClick={() => execute("italic")}><Italic size={16} /></ToolbarButton>
        </div>
        <div className="manual-si-visual-editor-toolgroup manual-si-text-format-toolgroup">
          <label className="manual-si-inline-format-control" title="Color del texto">
            <Palette size={15} />
            <span>Texto</span>
            <input
              type="color"
              value={selectedTextColor}
              disabled={disabled}
              aria-label="Color del texto"
              onMouseDown={captureTextColorTarget}
              onInput={(event: ChangeEvent<HTMLInputElement>) => applyTextColor(event.target.value, false)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => applyTextColor(event.target.value, true)}
            />
          </label>
          <label className="manual-si-inline-format-control" title="Tamaño del texto" onMouseDown={rememberSelection}>
            <Type size={15} />
            <span>Tamaño</span>
            <select
              value={selectedTextSize}
              disabled={disabled}
              aria-label="Tamaño del texto"
              onChange={(event: ChangeEvent<HTMLSelectElement>) => applyTextSize(Number(event.target.value))}
            >
              {TEXT_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size} pt</option>)}
            </select>
          </label>
        </div>
        <div className="manual-si-visual-editor-toolgroup">
          <ToolbarButton label="Lista con viñetas" disabled={disabled} onClick={() => execute("insertUnorderedList")}><List size={16} /></ToolbarButton>
          <ToolbarButton label="Lista numerada" disabled={disabled} onClick={() => execute("insertOrderedList")}><ListOrdered size={16} /></ToolbarButton>
        </div>
        <div className="manual-si-visual-editor-toolgroup">
          <ToolbarButton
            label="Insertar tabla"
            disabled={disabled}
            active={tableBuilderOpen}
            onClick={() => {
              rememberSelection();
              setTableToolbarPanel(null);
              setTableBuilderOpen((current) => !current);
            }}
          >
            <Table2 size={16} />
          </ToolbarButton>
        </div>

        {tableContext.active ? (
          <>
            <div className="manual-si-visual-editor-toolgroup manual-si-table-toolbar-group" aria-label="Herramientas de tabla">
              <div className="manual-si-toolbar-popover-wrap">
                <ToolbarButton
                  label="Herramientas de tabla"
                  disabled={disabled}
                  active={tableToolbarPanel === "table"}
                  onClick={() => setTableToolbarPanel((current) => current === "table" ? null : "table")}
                >
                  <Table2 size={16} />
                </ToolbarButton>
                {tableToolbarPanel === "table" ? (
                  <div className="manual-si-toolbar-popover manual-si-toolbar-popover-wide" role="dialog" aria-label="Herramientas de tabla">
                    <div className="manual-si-toolbar-popover-heading">
                      <strong>Tabla</strong>
                      <span>{selectedTableCellCount > 0 ? `${selectedTableCellCount} celdas seleccionadas` : "Celda activa"}</span>
                    </div>
                    <div className="manual-si-toolbar-popover-section">
                      <span className="manual-si-toolbar-popover-label">Selección</span>
                      <div className="manual-si-toolbar-menu-actions">
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={selectCurrentRow}><Rows3 size={14} /> Fila</button>
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={selectCurrentColumn}><Columns3 size={14} /> Columna</button>
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={selectEntireTable}><Table2 size={14} /> Tabla</button>
                        <button type="button" disabled={disabled || selectedTableCellCount === 0} onMouseDown={(event) => event.preventDefault()} onClick={clearTableSelection}>Limpiar</button>
                      </div>
                    </div>
                    <div className="manual-si-toolbar-popover-section">
                      <span className="manual-si-toolbar-popover-label">Celdas y estilo</span>
                      <div className="manual-si-toolbar-menu-actions">
                        <button type="button" disabled={disabled || selectedTableCellCount < 2} onMouseDown={(event) => event.preventDefault()} onClick={mergeSelectedCells}>Combinar</button>
                        <button type="button" disabled={disabled || !tableContext.canUnmergeCell} onMouseDown={(event) => event.preventDefault()} onClick={unmergeCurrentCell}>Separar</button>
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={copyCellStyle}>Copiar estilo</button>
                        <button type="button" disabled={disabled || !hasCopiedCellStyle} onMouseDown={(event) => event.preventDefault()} onClick={pasteCellStyle}>Pegar estilo</button>
                      </div>
                    </div>
                    <div className="manual-si-toolbar-popover-section">
                      <span className="manual-si-toolbar-popover-label">Estructura</span>
                      <div className="manual-si-toolbar-menu-actions">
                        <button type="button" title={!tableContext.canAddRow ? "Esta posición atraviesa una combinación vertical. Selecciona otra fila o separa las celdas implicadas." : undefined} disabled={disabled || !tableContext.canAddRow} onMouseDown={(event) => event.preventDefault()} onClick={addTableRow}><Plus size={14} /> Fila</button>
                        <button type="button" title={!tableContext.canDeleteRow ? "No se puede quitar la cabecera ni una fila incluida en una combinación vertical." : undefined} disabled={disabled || !tableContext.canDeleteRow} onMouseDown={(event) => event.preventDefault()} onClick={deleteTableRow}><Trash2 size={14} /> Fila</button>
                        <button type="button" title={!tableContext.canAddColumn ? "Esta posición atraviesa una combinación horizontal. Selecciona otra columna o separa las celdas implicadas." : undefined} disabled={disabled || !tableContext.canAddColumn} onMouseDown={(event) => event.preventDefault()} onClick={addTableColumn}><Plus size={14} /> Columna</button>
                        <button type="button" title={!tableContext.canDeleteColumn ? "No se puede quitar la última columna ni una columna incluida en una combinación horizontal." : undefined} disabled={disabled || !tableContext.canDeleteColumn} onMouseDown={(event) => event.preventDefault()} onClick={deleteTableColumn}><Trash2 size={14} /> Columna</button>
                        <button type="button" className="is-danger" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={deleteTable}><Trash2 size={14} /> Eliminar tabla</button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="manual-si-toolbar-popover-wrap">
                <ToolbarButton
                  label="Color de fondo"
                  disabled={disabled}
                  active={tableToolbarPanel === "background"}
                  onClick={() => setTableToolbarPanel((current) => current === "background" ? null : "background")}
                >
                  <Palette size={16} />
                </ToolbarButton>
                {tableToolbarPanel === "background" ? (
                  <div className="manual-si-toolbar-popover" role="dialog" aria-label="Color de fondo de celdas">
                    <div className="manual-si-toolbar-popover-heading"><strong>Color de fondo</strong></div>
                    <label className="manual-si-toolbar-field">
                      <span>Aplicar a</span>
                      <select value={tableColorScope} disabled={disabled} onChange={(event: ChangeEvent<HTMLSelectElement>) => setTableColorScope(event.target.value as TableColorScope)}>
                        <option value="cell">Celda</option>
                        <option value="selection" disabled={selectedTableCellCount === 0}>Selección múltiple{selectedTableCellCount > 0 ? ` (${selectedTableCellCount})` : ""}</option>
                        <option value="row">Fila</option>
                        <option value="column">Columna</option>
                        <option value="table">Tabla completa</option>
                      </select>
                    </label>
                    <label className="manual-si-toolbar-field manual-si-toolbar-color-field">
                      <span>Color</span>
                      <input type="color" value={selectedColor} disabled={disabled} onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedColor(event.target.value)} />
                    </label>
                    <div className="manual-si-toolbar-popover-footer">
                      <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyTableColor(false)}>Aplicar</button>
                      <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyTableColor(true)}>Quitar color</button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="manual-si-toolbar-popover-wrap">
                <ToolbarButton
                  label="Bordes de celdas"
                  disabled={disabled}
                  active={tableToolbarPanel === "border"}
                  onClick={() => setTableToolbarPanel((current) => current === "border" ? null : "border")}
                >
                  <Rows3 size={16} />
                </ToolbarButton>
                {tableToolbarPanel === "border" ? (
                  <div className="manual-si-toolbar-popover" role="dialog" aria-label="Bordes de celdas">
                    <div className="manual-si-toolbar-popover-heading"><strong>Bordes</strong></div>
                    <label className="manual-si-toolbar-field manual-si-toolbar-color-field">
                      <span>Color</span>
                      <input type="color" value={selectedBorderColor} disabled={disabled} onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedBorderColor(event.target.value)} />
                    </label>
                    <label className="manual-si-toolbar-field">
                      <span>Grosor</span>
                      <select value={selectedBorderWidth} disabled={disabled} onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedBorderWidth(Number(event.target.value))}>
                        {[1, 2, 3, 4, 5, 6, 8].map((width) => <option key={width} value={width}>{width} px</option>)}
                      </select>
                    </label>
                    <div className="manual-si-toolbar-popover-footer">
                      <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyTableBorder(false)}>Aplicar</button>
                      <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyTableBorder(true)}>Sin borde</button>
                    </div>
                  </div>
                ) : null}
              </div>

              <ToolbarButton label="Alinear a la izquierda" disabled={disabled} onClick={() => applyCellTextAlignment("left")}><AlignLeft size={16} /></ToolbarButton>
              <ToolbarButton label="Centrar texto" disabled={disabled} onClick={() => applyCellTextAlignment("center")}><AlignCenter size={16} /></ToolbarButton>
              <ToolbarButton label="Alinear a la derecha" disabled={disabled} onClick={() => applyCellTextAlignment("right")}><AlignRight size={16} /></ToolbarButton>

              <div className="manual-si-toolbar-popover-wrap">
                <ToolbarButton
                  label="Tamaño de celdas"
                  disabled={disabled}
                  active={tableToolbarPanel === "size"}
                  onClick={() => setTableToolbarPanel((current) => current === "size" ? null : "size")}
                >
                  <Columns3 size={16} />
                </ToolbarButton>
                {tableToolbarPanel === "size" ? (
                  <div className="manual-si-toolbar-popover manual-si-toolbar-popover-size" role="dialog" aria-label="Tamaño de celdas">
                    <div className="manual-si-toolbar-popover-heading">
                      <strong>Tamaño de celdas</strong>
                      <span>También puedes arrastrar los bordes de la selección. Una selección parcial cambia solo esas celdas; una columna completa cambia toda la columna.</span>
                    </div>
                    <div className="manual-si-toolbar-size-grid">
                      <label className="manual-si-toolbar-field">
                        <span>Ancho de celda/selección</span>
                        <input type="number" min={40} max={1200} step={10} value={selectedColumnWidth} disabled={disabled} onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedColumnWidth(Number(event.target.value) || 40)} />
                      </label>
                      <div className="manual-si-toolbar-size-actions">
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyColumnWidth(false)}>Aplicar</button>
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyColumnWidth(true)}>Automático</button>
                      </div>
                      <label className="manual-si-toolbar-field">
                        <span>Altura de fila</span>
                        <input type="number" min={24} max={600} step={4} value={selectedRowHeight} disabled={disabled} onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedRowHeight(Number(event.target.value) || 24)} />
                      </label>
                      <div className="manual-si-toolbar-size-actions">
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyRowHeight(false)}>Aplicar</button>
                        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyRowHeight(true)}>Automático</button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}

        <div className="manual-si-visual-editor-toolgroup">
          <ToolbarButton label="Deshacer" disabled={disabled || !canUndo} onClick={undoHistory}><Undo2 size={16} /></ToolbarButton>
          <ToolbarButton label="Rehacer" disabled={disabled || !canRedo} onClick={redoHistory}><Redo2 size={16} /></ToolbarButton>
        </div>
      </div>

      {tableBuilderOpen ? (
        <div className="manual-si-table-builder">
          <div>
            <strong>Nueva tabla</strong>
            <span>Indica el tamaño inicial. Después podrás añadir o quitar filas y columnas.</span>
          </div>
          <label>
            <span>Filas</span>
            <input type="number" min={2} max={12} value={tableRows} onChange={(event: ChangeEvent<HTMLInputElement>) => setTableRows(Number(event.target.value) || 2)} />
          </label>
          <label>
            <span>Columnas</span>
            <input type="number" min={1} max={8} value={tableColumns} onChange={(event: ChangeEvent<HTMLInputElement>) => setTableColumns(Number(event.target.value) || 1)} />
          </label>
          <button type="button" className="manual-si-table-builder-create" onClick={insertTable}>
            <Plus size={15} /> Crear tabla
          </button>
        </div>
      ) : null}

      {tableContext.active && tableFeedback ? (
        <div className="manual-si-table-feedback" role="status">{tableFeedback}</div>
      ) : null}

      <div
        ref={editorRef}
        className="manual-si-visual-editor-surface"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Contenido del apartado"
        data-placeholder="Escribe aquí el contenido del apartado..."
        onInput={commitEditorChange}
        onFocus={() => {
          rememberSelection();
          syncTableContext(selectedCellRef.current ?? undefined);
        }}
        onMouseDown={handleSurfaceMouseDown}
        onMouseMove={handleSurfaceMouseMove}
        onMouseUp={(event: ReactMouseEvent<HTMLDivElement>) => {
          const wasTableDrag = dragSelectionRef.current !== null;
          const mouseUpCell = cellFromEventTarget(event.target);
          handleSurfaceMouseUp(event);
          if (!wasTableDrag && (!mouseUpCell || editingCellRef.current)) {
            rememberSelection();
            window.requestAnimationFrame(() => rememberSelection());
          }
        }}
        onSelect={rememberSelection}
        onDoubleClick={handleSurfaceDoubleClick}
        onKeyDown={handleSurfaceKeyDown}
        onKeyUp={() => {
          rememberSelection();
          syncTableContext(selectedCellRef.current ?? undefined);
        }}
        onBlur={rememberSelection}
        onPaste={(event: ClipboardEvent<HTMLDivElement>) => {
          if (selectedCellRef.current?.closest("table") && !editingCellRef.current) {
            event.preventDefault();
            setTableFeedback("Haz doble clic dentro de la celda para pegar texto. Para copiar formato usa Copiar estilo y Pegar estilo.");
            return;
          }
          event.preventDefault();
          const text = event.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
          window.requestAnimationFrame(() => {
            rememberSelection();
            syncTableContext(selectedCellRef.current ?? undefined);
            commitEditorChange();
          });
        }}
      />
      {tableResizeOverlay && tableContext.active && !disabled ? (
        <div
          className="manual-si-table-resize-overlay"
          style={{
            left: `${tableResizeOverlay.left}px`,
            top: `${tableResizeOverlay.top}px`,
            width: `${tableResizeOverlay.width}px`,
            height: `${tableResizeOverlay.height}px`,
          }}
          aria-hidden="true"
        >
          <div
            className="manual-si-table-resize-handle is-width"
            title="Arrastra para cambiar el ancho de las celdas seleccionadas"
            onMouseDown={(event: ReactMouseEvent<HTMLDivElement>) => beginTableResize("width", event)}
          />
          <div
            className="manual-si-table-resize-handle is-height"
            title="Arrastra para cambiar la altura de las filas seleccionadas"
            onMouseDown={(event: ReactMouseEvent<HTMLDivElement>) => beginTableResize("height", event)}
          />
        </div>
      ) : null}
      <div className="manual-si-visual-editor-help">
        Edita el contenido como en un documento normal. En tablas, haz clic o arrastra para seleccionar celdas como en Excel; arrastra el borde derecho o inferior de la selección para ajustar ancho o altura, y haz doble clic dentro de una celda para editar su texto.
      </div>
    </div>
  );
}
